import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { SearchableSelect } from "@/components/ui/SearchableSelect";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { 
  CalendarClock, 
  Plus, 
  Trash2, 
  Users, 
  TreePine, 
  Calendar as CalendarIcon,
  AlertTriangle,
  CheckCircle,
  Clock,
  BarChart3,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  Search,
  Eye,
  GripVertical,
  X,
  Save,
  XCircle,
  Loader2,
  Wifi,
  WifiOff,
  RotateCcw
} from "lucide-react";
import { format, addDays, startOfWeek, endOfWeek, eachDayOfInterval, parseISO } from "date-fns";
import { tr } from "date-fns/locale";
import { useMesaiSSE } from '../../hooks/useSSE';
import { normalizeSearchText } from '@/utils/stringUtils';

// Sabitler ve yardımcı bileşenler - ayrı modüllerden import
import { 
  API, 
  priorityColors, 
  dayTypeLabels, 
  dayTypeColors, 
  mesaiRoles, 
  roleLabels,
  roleTitles,
  managerRoles,
  highLevelRoles,
  basicRoles
} from '../mesai/mesaiConstants';
import { 
  getCurrentUser,
  getRoleTitle,
  sortDates,
  hasUserResponded,
  isCreatorHighLevel,
  isViewerLowerLevel,
  checkPreviewMode,
  getPlanStatusInfo
} from '../mesai/mesaiHelpers';
import DraggableModal from '../mesai/DraggableModal';
import MesaiAnketiSection from '../mesai/MesaiAnketiSection';
import TaslakPlanlarSection from '../mesai/TaslakPlanlarSection';
import MesaiRaporuSection from '../mesai/MesaiRaporuSection';
import MesaiTakvimiSection from '../mesai/MesaiTakvimiSection';
import AddHolidayDialog from '../mesai/AddHolidayDialog';
import ResmiTatillerSection from '../mesai/ResmiTatillerSection';
import ParkOncelikleriSection from '../mesai/ParkOncelikleriSection';
import { useRoleFlags } from '@/auth/useRoleFlags';

export default function MesaiYonetimiTab({ userRole, personnelId }) {
  // Sprint 2 F3: Dinamik yetki + rol flag'leri (intermediate)
  const flags = useRoleFlags(userRole);
  const { has, isAdmin, isMudur, isMudurYardimcisi, isBolgeMuhendisi, isFormen, isParkGorevlisi, isGolgeAdmin } = flags;

  const [activeSubTab, setActiveSubTab] = useState('survey');
  const [loading, setLoading] = useState(false);
  
  // Badge sayıları için state
  const [activeSurveysBadge, setActiveSurveysBadge] = useState(0);
  const [pendingPlansBadge, setPendingPlansBadge] = useState(0);
  
  // SSE bağlantı durumu
  const [sseConnected, setSseConnected] = useState(false);
  
  // SSE güncellemelerini tetiklemek için ref'ler
  const refreshSurveysRef = useRef(null);
  const refreshPlansRef = useRef(null);
  const refreshBadgesRef = useRef(null);
  
  // Temel rol kontrolü — park_gorevlisi/cim_ekibi için kısıtlı erişim
  // (isParkGorevlisi flag'i çim_ekibi/cim_ekibi/gorevli kapsıyor)
  const isBasicRole = isParkGorevlisi;
  // isGolgeAdmin yukarıda useRoleFlags'ten geliyor — burada tekrar tanım yok
  const canMutateMesai = !isBasicRole && !isGolgeAdmin;

  // Bir mesai planını (duty_schedule) bu kullanıcı SİLEBİLİR/yönetebilir mi?
  // Rol yetkisi (canMutateMesai) + HİYERARŞİK KAPSAM birlikte: müdür/admin tümü;
  // MY/BM/formen yalnızca kendine bağlı (planın formen/BM zinciri) ya da kendi
  // oluşturduğu planlar. Kapsam dışıysa "Tüm Mesai Planını Sil" gizlenir.
  const canMutateScheduleScope = (schedule) => {
    if (!schedule || !canMutateMesai) return false;
    if (isAdmin || isMudur) return true;
    if (schedule.created_by && schedule.created_by === personnelId) return true;
    const formenId = schedule.formen_id || schedule.aksam_schedule?.formen_id || schedule.gece_schedule?.formen_id;
    const bmId = schedule.bolge_muh_id || schedule.aksam_schedule?.bolge_muh_id || schedule.gece_schedule?.bolge_muh_id;
    const formen = personnel.find(f => f.id === formenId);
    if (isFormen) return formenId === personnelId;
    if (isBolgeMuhendisi) return bmId === personnelId || formen?.bolge_muhendisi_id === personnelId;
    if (isMudurYardimcisi) {
      if (formen?.mudur_yardimcisi_id === personnelId) return true;
      const bm = personnel.find(b => b.id === (formen?.bolge_muhendisi_id || bmId));
      return bm?.mudur_yardimcisi_id === personnelId;
    }
    return false;
  };

  // Anket → Taslak Plan Köprüsü
  // Anket sonuçlarından "Plan Oluştur" tıklandığında bu state dolacak
  const [planFromSurvey, setPlanFromSurvey] = useState(null);
  // { surveyId, surveyTitle, targetDate, availablePersonnel: [{id, name, role}] }
  
  // Mesai Takvimi State
  const [currentWeekStart, setCurrentWeekStart] = useState(startOfWeek(new Date(), { weekStartsOn: 1 }));
  const [dutySchedules, setDutySchedules] = useState([]);
  const [parks, setParks] = useState([]);
  const [personnel, setPersonnel] = useState([]);
  /** Tüm personel (aktif + pasif) - Mesai raporunda "Toplam Personel" sayısı için */
  const [allPersonnel, setAllPersonnel] = useState([]);
  const [holidays, setHolidays] = useState([]);
  const [holidayAudit, setHolidayAudit] = useState([]);
  
  // Day Click Dialog State
  const [showDayDialog, setShowDayDialog] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [dialogMode, setDialogMode] = useState('view');
  
  // Holiday Form State
  const [holidayName, setHolidayName] = useState('');
  const [holidayType, setHolidayType] = useState('idari_tatil');
  const [holidayDescription, setHolidayDescription] = useState('');
  const [holidayIsFullDay, setHolidayIsFullDay] = useState(true);
  const [holidayHalfPeriod, setHolidayHalfPeriod] = useState('afternoon'); // 'morning' | 'afternoon'
  const [holidayEditId, setHolidayEditId] = useState(null); // dolu → mevcut tatili düzenleme modu
  
  // Duty Form State
  const [selectedPark, setSelectedPark] = useState(null);
  const [selectedPersonnel, setSelectedPersonnel] = useState([]);
  const [shiftType, setShiftType] = useState('full_day'); // full_day veya hourly
  const [startTime, setStartTime] = useState('17:00');
  const [endTime, setEndTime] = useState('20:00');
  const [personnelRoleFilter, setPersonnelRoleFilter] = useState('all');
  
  // Akşam/Gece Mesaisi Dialog State
  const [overtimeNote, setOvertimeNote] = useState(''); // Opsiyonel açıklama
  const [overtimeFormenJoining, setOvertimeFormenJoining] = useState(false); // Formen katılıyor mu
  const [overtimeFormenNotJoiningReason, setOvertimeFormenNotJoiningReason] = useState('');
  const [overtimeBolgeMuhJoining, setOvertimeBolgeMuhJoining] = useState(false); // Bölge müh katılıyor mu
  const [overtimeBolgeMuhNotJoiningReason, setOvertimeBolgeMuhNotJoiningReason] = useState('');
  const [selectedOvertimeFormen, setSelectedOvertimeFormen] = useState(null); // Müdür yrd. için formen seçimi
  const [selectedOvertimeBolgeMuh, setSelectedOvertimeBolgeMuh] = useState(null); // Müdür yrd. için bölge müh seçimi
  const [overtimePersonnelSearch, setOvertimePersonnelSearch] = useState(''); // Personel arama
  const [overtimeUseOther, setOvertimeUseOther] = useState(false); // Diğer (park dışı) seçeneği
  const [overtimeOtherDescription, setOvertimeOtherDescription] = useState(''); // Diğer açıklaması
  
  // Add Holiday Dialog
  const [showAddHolidayDialog, setShowAddHolidayDialog] = useState(false);
  const [newHolidayName, setNewHolidayName] = useState('');
  const [newHolidayDate, setNewHolidayDate] = useState('');
  const [newHolidayType, setNewHolidayType] = useState('resmi_tatil');
  const [newHolidayRecurring, setNewHolidayRecurring] = useState(true);
  const [newHolidayIsFullDay, setNewHolidayIsFullDay] = useState(true);
  const [newHolidayHalfPeriod, setNewHolidayHalfPeriod] = useState('afternoon');
  
  // Schedule Detail Dialog - Tek bir mesai planının detayını göstermek için
  const [showScheduleDetailDialog, setShowScheduleDetailDialog] = useState(false);
  const [selectedScheduleDetail, setSelectedScheduleDetail] = useState(null);
  const [selectedScheduleDate, setSelectedScheduleDate] = useState(null);
  
  // Mesai İptal Dialog State
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [cancelTargetRecord, setCancelTargetRecord] = useState(null); // İptal edilecek kayıt
  const [cancelReason, setCancelReason] = useState('');
  const [cancelNote, setCancelNote] = useState('');
  
  // Supervisor (Formen/BM) İptal Dialog State
  const [showSupervisorCancelDialog, setShowSupervisorCancelDialog] = useState(false);
  const [supervisorCancelTarget, setSupervisorCancelTarget] = useState(null);
  const [supervisorCancelReason, setSupervisorCancelReason] = useState('');
  const [supervisorCancelNote, setSupervisorCancelNote] = useState('');
  
  // Supervisor Detay Dialog State
  const [showSupervisorDetailDialog, setShowSupervisorDetailDialog] = useState(false);
  const [supervisorDetailRecords, setSupervisorDetailRecords] = useState([]);
  const [supervisorDetailDate, setSupervisorDetailDate] = useState(null);
  const [supervisorDetailRole, setSupervisorDetailRole] = useState(null); // formen veya bolge_muhendisi
  
  // Mobil Ekip Dialog State
  const [showMobileTeamDialog, setShowMobileTeamDialog] = useState(false);
  const [mobileTeamDate, setMobileTeamDate] = useState(null);
  const [mobileTeamPersonnel, setMobileTeamPersonnel] = useState([]); // Seçilen personeller
  const [mobileTeamNote, setMobileTeamNote] = useState('');
  const [mobileTeamStartTime, setMobileTeamStartTime] = useState('08:00');
  const [mobileTeamEndTime, setMobileTeamEndTime] = useState('17:00');
  const [mobilePersonnelSearch, setMobilePersonnelSearch] = useState('');
  const [selectedMobileFormen, setSelectedMobileFormen] = useState(null); // Bölge müh./Müdür yrd. için formen seçimi
  const [selectedMobileBolgeMuh, setSelectedMobileBolgeMuh] = useState(null); // Müdür/Müdür yrd. için bölge müh. seçimi
  // Formen katılım durumu
  const [formenJoining, setFormenJoining] = useState(true); // Formen mesaiye katılıyor mu?
  const [formenNotJoiningReason, setFormenNotJoiningReason] = useState(''); // Katılmıyorsa sebep
  // Bölge mühendisi katılım durumu (mobil ekip oluştururken)
  const [bolgeMuhJoining, setBolgeMuhJoining] = useState(false); // Bölge müh. mesaiye katılıyor mu?
  const [bolgeMuhNotJoiningReason, setBolgeMuhNotJoiningReason] = useState('');
  
  // Park Search & Filter State
  const [parkSearchTerm, setParkSearchTerm] = useState('');
  const [selectedPriorityFilter, setSelectedPriorityFilter] = useState(null); // Öncelik filtreleme
  
  // Overtime Summary State
  const [overtimeSummary, setOvertimeSummary] = useState(null);
  const [overtimeRecords, setOvertimeRecords] = useState([]);
  const [reportStartDate, setReportStartDate] = useState(format(new Date(new Date().getFullYear(), 0, 1), 'yyyy-MM-dd'));
  const [reportEndDate, setReportEndDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  
  // Mesai raporu personel arama filtresi
  const [reportPersonnelSearch, setReportPersonnelSearch] = useState('');
  // Mesai raporu sıralama
  const [reportSortBy, setReportSortBy] = useState('hours_desc'); // hours_desc, hours_asc, name_asc
  // Mesai raporu rol filtresi (bölge mühendisi / formen seçimi)
  const [reportRoleFilter, setReportRoleFilter] = useState('all'); // all, veya personel id'si
  
  // Personnel Detail Dialog
  const [showPersonnelDetailDialog, setShowPersonnelDetailDialog] = useState(false);
  const [selectedPersonnelDetail, setSelectedPersonnelDetail] = useState(null);
  const [selectedPersonnelRecords, setSelectedPersonnelRecords] = useState([]);
  const [detailFilterType, setDetailFilterType] = useState('all');
  
  // Suggested Personnel
  const [suggestedPersonnel, setSuggestedPersonnel] = useState([]);

  // Delete Confirmation State
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleteInProgress, setDeleteInProgress] = useState(false);
  const [deleteType, setDeleteType] = useState(null); // 'duty', 'holiday', 'holidayFromDay', 'holidayAudit'
  const [deleteItemId, setDeleteItemId] = useState(null);
  const [deleteItemName, setDeleteItemName] = useState('');

  // Fetch functions
  const fetchParks = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/api/parks`);
      setParks(response.data);
    } catch (error) {
      console.error('Parks fetch error:', error);
    }
  }, []);

  const fetchPersonnel = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/api/personnel`);
      const all = response.data || [];
      // Aktif personel: dropdown ve tablo için
      setPersonnel(all.filter(p => p.status === 'active'));
      // Tüm personel: Mesai raporunda "Toplam Personel" sayısı için (100+ göstermek üzere)
      setAllPersonnel(all);
    } catch (error) {
      console.error('Personnel fetch error:', error);
    }
  }, []);

  // Badge sayılarını hesapla (panel kullanıcısına göre; çoklu sekme için personnelId prop kullan)
  const fetchBadgeCounts = useCallback(async () => {
    try {
      const auth = JSON.parse(localStorage.getItem('parkBakimAuth') || '{}');
      const myId = personnelId || auth.personnelId;
      const myRole = userRole || auth.userRole;
      
      // Personnel bilgilerini al (görünürlük kontrolü için)
      const personnelResponse = await axios.get(`${API}/api/personnel`);
      const allPersonnel = personnelResponse.data;
      const currentPerson = allPersonnel.find(p => p.id === myId);
      const isUnderFormen = (formenId) =>
        !!formenId && (
          currentPerson?.formen_id === formenId ||
          currentPerson?.parent_id === formenId
        );
      
      // Aktif anket sayısı - sadece kullanıcının görebildiği anketler
      const surveysResponse = await axios.get(`${API}/api/overtime-surveys?status=active`);
      
      // Görünürlük filtresi - HİYERARŞİK HEDEFLEME DAHİL
      const visibleSurveys = surveysResponse.data.filter(survey => {
        // Kendi oluşturduğu anketi her zaman görebilir
        if (survey.created_by === myId) return true;
        
        const creator = allPersonnel.find(p => p.id === survey.created_by);
        if (!creator) return false;
        
        const creatorRole = survey.created_by_role || creator.role;
        
        // === YENİ: HİYERARŞİK HEDEFLEME KONTROLÜ ===
        // Eğer anket belirli bir formen veya BM'ye hedeflenmiş ise
        
        // Hedef Formen belirlenmişse
        if (survey.target_formen_id) {
          // Ben hedef formen miyim?
          if (myId === survey.target_formen_id) return true;
          // Ben hedef formenin ekibinden miyim?
          if (isUnderFormen(survey.target_formen_id)) return true;
          // Hedef BM de belirlenmişse, ben o BM miyim?
          if (survey.target_bolge_muhendisi_id && myId === survey.target_bolge_muhendisi_id) return true;
          // Müdür/Müdür Yrd. özel durumu - sadece görüntüleme yetkisi, bildirim gitmez
          if (['mudur', 'mudur_yardimcisi'].includes(myRole)) {
            // Müdür yardımcısı sadece kendi bölgesindeki hedefli anketi görebilir (ama bildirim almaz)
            // Müdür hepsini görebilir (ama bildirim almaz)
            // Bu durum badge'de sayılmayacak, aşağıda return false ile engelleniyor
            return false; // Badge'de sayılmaz
          }
          // Diğer durumlarda hedefli anketi gösterme
          return false;
        }
        
        // Hedef BM belirlenmişse ama formen belirlenmemişse
        if (survey.target_bolge_muhendisi_id && !survey.target_formen_id) {
          // Ben hedef BM miyim?
          if (myId === survey.target_bolge_muhendisi_id) return true;
          // Ben hedef BM'nin altındaki biriyim?
          if (currentPerson?.bolge_muhendisi_id === survey.target_bolge_muhendisi_id) return true;
          // Müdür/Müdür Yrd. özel durumu
          if (['mudur', 'mudur_yardimcisi'].includes(myRole)) {
            return false; // Badge'de sayılmaz
          }
          return false;
        }
        
        // === ESKİ MANTIK: Hedef belirlenmemişse oluşturana göre ===
        
        // Alt kademelerin görebilmesi
        if (creatorRole === 'formen' && isUnderFormen(survey.created_by)) return true;
        if (creatorRole === 'bolge_muhendisi' && currentPerson?.bolge_muhendisi_id === survey.created_by) return true;
        if (creatorRole === 'mudur_yardimcisi' && currentPerson?.mudur_yardimcisi_id === survey.created_by) return true;
        if (creatorRole === 'mudur') return true;
        
        // Üst kademelerin görebilmesi
        if (creatorRole === 'formen') {
          if (creator.bolge_muhendisi_id === myId) return true;
          if (creator.mudur_yardimcisi_id === myId) return true;
          if (myRole === 'mudur') return true;
        }
        if (creatorRole === 'bolge_muhendisi') {
          if (creator.mudur_yardimcisi_id === myId) return true;
          if (myRole === 'mudur') return true;
        }
        if (creatorRole === 'mudur_yardimcisi' && myRole === 'mudur') return true;
        
        return false;
      });
      
      // Görülebilir ve henüz görülmemiş olanları say
      const unseenSurveys = visibleSurveys.filter(survey => 
        !survey.viewed_by || !survey.viewed_by.includes(myId)
      );
      setActiveSurveysBadge(unseenSurveys.length);
      
      // Onay bekleyen plan sayısı (görülmemiş olanlar) - hedef filtresi ekle
      const plansResponse = await axios.get(`${API}/api/overtime-draft-plans`);
      const pendingStatuses = ['onay_bekliyor', 'bolge_muhendisi_onayladi', 'mudur_yardimcisi_onayladi'];
      
      // Planları hedef filtresi ile filtrele
      const visiblePlans = plansResponse.data.filter(plan => {
        // Kendi oluşturduğu planı her zaman görebilir
        if (plan.created_by === myId) return true;
        
        // Hedefli plan kontrolü (survey'den gelen hedef bilgisi)
        if (plan.target_formen_id) {
          // Ben hedef formen miyim?
          if (myId === plan.target_formen_id) return true;
          // Ben hedef formenin ekibinden miyim?
          if (isUnderFormen(plan.target_formen_id)) return true;
          // Hedef BM miyim?
          if (plan.target_bolge_muhendisi_id && myId === plan.target_bolge_muhendisi_id) return true;
          // Müdür/Müdür Yrd. badge'de sayılmaz
          if (['mudur', 'mudur_yardimcisi'].includes(myRole)) return false;
          return false;
        }
        
        // Hedef BM belirlenmişse ama formen belirlenmemişse
        if (plan.target_bolge_muhendisi_id && !plan.target_formen_id) {
          if (myId === plan.target_bolge_muhendisi_id) return true;
          if (currentPerson?.bolge_muhendisi_id === plan.target_bolge_muhendisi_id) return true;
          if (['mudur', 'mudur_yardimcisi'].includes(myRole)) return false;
          return false;
        }
        
        // Hedef belirlenmemişse - eski mantık
        const creatorRole = plan.created_by_role;
        if (creatorRole === 'formen' && isUnderFormen(plan.created_by)) return true;
        if (creatorRole === 'bolge_muhendisi' && currentPerson?.bolge_muhendisi_id === plan.created_by) return true;
        if (creatorRole === 'mudur_yardimcisi' && currentPerson?.mudur_yardimcisi_id === plan.created_by) return true;
        if (creatorRole === 'mudur') return true;
        
        // Üst kademeler
        const creator = allPersonnel.find(p => p.id === plan.created_by);
        if (creator) {
          if (creatorRole === 'formen') {
            if (creator.bolge_muhendisi_id === myId) return true;
            if (creator.mudur_yardimcisi_id === myId) return true;
            if (myRole === 'mudur') return true;
          }
          if (creatorRole === 'bolge_muhendisi') {
            if (creator.mudur_yardimcisi_id === myId) return true;
            if (myRole === 'mudur') return true;
          }
        }
        
        return false;
      });
      
      const pendingPlans = visiblePlans.filter(plan => 
        pendingStatuses.includes(plan.status) &&
        (!plan.viewed_by || !plan.viewed_by.includes(myId))
      );
      setPendingPlansBadge(pendingPlans.length);
    } catch (error) {
      console.error('Badge counts fetch error:', error);
    }
  }, [personnelId, userRole]);

  const fetchHolidays = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/api/official-holidays`);
      setHolidays(response.data);
    } catch (error) {
      console.error('Holidays fetch error:', error);
    }
  }, []);

  const getHolidayActorParams = useCallback(() => {
    const auth = JSON.parse(localStorage.getItem('parkBakimAuth') || '{}');
    return {
      actorId: auth.personnelId || personnelId || null,
      actorName: auth.personnelName || null,
      actorRole: auth.userRole || userRole || null
    };
  }, [personnelId, userRole]);

  const fetchHolidayAuditForDate = useCallback(async (dateStr) => {
    if (!dateStr) {
      setHolidayAudit([]);
      return;
    }
    try {
      const response = await axios.get(`${API}/api/official-holidays-audit`, {
        params: { date: dateStr, limit: 30 }
      });
      setHolidayAudit(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Holiday audit fetch error:', error);
      setHolidayAudit([]);
    }
  }, []);

  const fetchDutySchedules = useCallback(async () => {
    const weekEnd = endOfWeek(currentWeekStart, { weekStartsOn: 1 });
    const startDateStr = format(currentWeekStart, 'yyyy-MM-dd');
    const endDateStr = format(weekEnd, 'yyyy-MM-dd');

    try {
      // Normal nöbet çizelgelerini getir
      const dutyResponse = await axios.get(`${API}/api/duty-schedules`, {
        params: {
          start_date: startDateStr,
          end_date: endDateStr
        }
      });

      // Temel veri her durumda gösterilsin; ikinci çağrı düşse bile takvim boş kalmasın.
      const baseSchedules = Array.isArray(dutyResponse.data) ? dutyResponse.data : [];
      setDutySchedules(baseSchedules);

      // Supervisor (Formen/BM) mesai kayıtlarını getir (opsiyonel katman).
      const overtimeResponse = await axios.get(`${API}/api/overtime-records`, {
        params: {
          start_date: startDateStr,
          end_date: endDateStr
        }
      });

      // Supervisor kayıtlarını takvim formatına dönüştür
      // ÖNEMLİ: Aynı personel + aynı tarih için duplicate kayıtları filtrele
      // NOT: İptal edilenler de gösterilecek (üstü çizili olarak)
      const seenSupervisors = new Set();
      const overtimeRows = Array.isArray(overtimeResponse.data) ? overtimeResponse.data : [];
      const supervisorSchedules = overtimeRows
        .filter(r => {
          if (!r.is_supervisor) return false;
          const key = `${r.personnel_id}_${r.date}`;
          if (seenSupervisors.has(key)) return false;
          seenSupervisors.add(key);
          return true;
        })
        .map(r => ({
          id: r.id || r._id,
          date: r.date,
          park_name: r.park_name || (r.personnel_role === 'formen' ? '📋 Alan Denetimi' : '📋 Bölge Denetimi'),
          park_id: null,
          personnel_id: r.personnel_id,
          personnel_name: r.personnel_name,
          personnel_role: r.personnel_role,
          is_supervisor: true,
          is_supervisor_schedule: true, // Takvimde ayırt etmek için
          day_type: r.day_type,
          schedule_type: 'supervisor', // Özel tip
          hours: r.hours_worked || r.hours || 8,
          start_time: r.start_time || '08:00',
          end_time: r.end_time || '17:00',
          cancelled: r.cancelled || false,
          cancelled_by_name: r.cancelled_by_name,
          cancelled_at: r.cancelled_at,
          cancel_reason: r.cancel_reason,
          cancel_note: r.cancel_note
        }));

      // İki listeyi birleştir
      setDutySchedules([...baseSchedules, ...supervisorSchedules]);
      return true;
    } catch (error) {
      console.error('Duty schedules fetch error:', error);
      // İlk çağrı başarısızsa bir kez hızlı retry dene.
      try {
        const retry = await axios.get(`${API}/api/duty-schedules`, {
          params: { start_date: startDateStr, end_date: endDateStr }
        });
        const retryRows = Array.isArray(retry.data) ? retry.data : [];
        setDutySchedules(retryRows);
        return true;
      } catch (retryError) {
        console.error('Duty schedules retry error:', retryError);
      }
      // Geçici ağ/API hatalarında takvim boşta kalmasın diye çağırana durum döndür.
      return false;
    }
  }, [currentWeekStart]);

  // SSE (Server-Sent Events) - Gerçek zamanlı güncellemeler
  const { isConnected: sseIsConnected, reconnect: sseReconnect } = useMesaiSSE(personnelId, {
    onSurveyUpdate: useCallback((payload) => {
      console.log('[MesaiYonetimiTab] Anket güncellemesi alındı:', payload);
      // Anketleri yenile - MesaiAnketiSection içindeki fetchSurveys tetiklenecek
      window.dispatchEvent(new CustomEvent('refreshSurveys'));
      // Badge'leri güncelle
      fetchBadgeCounts();
      // Toast göster
      if (payload.action === 'created' && payload.created_by !== personnelId) {
        toast.info(`Yeni anket: ${payload.title || 'Mesai Anketi'}`);
      }
    }, [fetchBadgeCounts, personnelId]),
    onDraftPlanUpdate: useCallback((payload) => {
      console.log('[MesaiYonetimiTab] Plan güncellemesi alındı:', payload);
      // Planları yenile - TaslakPlanlarSection içindeki fetchDraftPlans tetiklenecek
      window.dispatchEvent(new CustomEvent('refreshDraftPlans'));
      // Badge'leri güncelle
      fetchBadgeCounts();
      // Onaylanan/oluşturulan planlar takvime de anında yansısın.
      if (activeSubTab === 'schedule') {
        fetchDutySchedules();
      }
      // Toast göster
      if (payload.action === 'approved') {
        toast.success('Plan onaylandı');
      } else if (payload.action === 'rejected') {
        toast.error('Plan reddedildi');
      } else if (payload.action === 'submitted' && payload.created_by !== personnelId) {
        toast.info('Yeni plan onaya gönderildi');
      }
    }, [fetchBadgeCounts, fetchDutySchedules, activeSubTab, personnelId]),
    onHolidayUpdate: useCallback((payload) => {
      console.log('[MesaiYonetimiTab] Tatil güncellemesi alındı:', payload);
      fetchHolidays();
      // Takvim görünümünde isek aynı anda çizelgeyi de yenile.
      if (activeSubTab === 'schedule') {
        fetchDutySchedules();
      }
    }, [fetchHolidays, fetchDutySchedules, activeSubTab]),
    onNotification: useCallback((payload) => {
      console.log('[MesaiYonetimiTab] Bildirim alındı:', payload);
      toast.info(payload.message || 'Yeni bildirim');
    }, [])
  });

  // SSE bağlantı durumunu state'e yansıt
  useEffect(() => {
    setSseConnected(sseIsConnected);
  }, [sseIsConnected]);

  const fetchOvertimeSummary = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/api/overtime-records`, {
        params: {
          start_date: reportStartDate,
          end_date: reportEndDate
        }
      });
      
      const records = response.data;
      setOvertimeRecords(records);
      // Tarih anahtarlarını normalize et: tatil `date`'i ISO zaman ekli
      // (2026-01-01T00:00:00) gelebilirken kayıt `date`'i düz YYYY-MM-DD olabilir;
      // takvim tarafı gibi burada da ilk 10 karakteri baz alıyoruz ki tatil tipi
      // override'ı ve arife (yarım gün) tavanı kayıtlarla eşleşsin.
      const normDateKey = (value) => String(value || '').slice(0, 10);
      const holidayTypeByDate = new Map(
        (Array.isArray(holidays) ? holidays : [])
          .filter(h => h?.date)
          .map((h) => [normDateKey(h.date), h.holiday_type || null])
      );
      // Yarım gün (arife) tatiller: o gün tatil sayısının yarısı kadar saat sayılır.
      const halfDayDates = new Set(
        (Array.isArray(holidays) ? holidays : [])
          .filter(h => h?.date && h.is_full_day === false)
          .map((h) => normDateKey(h.date))
      );
      const normalizeDayType = (value) => {
        const v = String(value || '').trim().toLowerCase();
        if (v === 'resmi') return 'resmi_tatil';
        if (v === 'idari') return 'idari_tatil';
        return v;
      };
      
      // Manuel özet hesaplama - Gün ve Saat ayrı ayrı
      // ÖNEMLİ: Aynı gün birden fazla parka çalışılabilir, bu durumda gün sadece 1 kez sayılmalı
      const summary = {};
      
      // Önce personel + tarih bazında grupla (aynı gün birden fazla kayıt olabilir - akşam+gece dahil)
      const personnelDateGroups = {};
      
      for (const record of records) {
        const pid = record.personnel_id;
        const date = record.date;
        // Tarih sonradan resmi/idari tatil olarak işaretlenmişse raporda gün tipini buna göre düzelt.
        const holidayType = normalizeDayType(holidayTypeByDate.get(normDateKey(date)));
        const effectiveDayType = holidayType || normalizeDayType(record.day_type);
        // Akşam/gece mesaisi için aynı tarih tek grup olmalı
        const key = `${pid}_${date}`;
        
        if (!personnelDateGroups[key]) {
          personnelDateGroups[key] = {
            personnel_id: pid,
            personnel_name: record.personnel_name,
            date: date,
            day_types: new Set(), // Birden fazla day_type olabilir (akşam + gece)
            records: [],
            cancelled_records: [],
            aksam_records: [],
            gece_records: []
          };
        }
        
        personnelDateGroups[key].day_types.add(effectiveDayType);
        
        // İptal edilen ve edilmeyen kayıtları ayrı tut
        if (record.cancelled) {
          personnelDateGroups[key].cancelled_records.push({ ...record, effective_day_type: effectiveDayType });
        } else {
          personnelDateGroups[key].records.push({ ...record, effective_day_type: effectiveDayType });
          // Akşam/gece kayıtlarını ayrıca takip et
          if (effectiveDayType === 'aksam_mesaisi') {
            personnelDateGroups[key].aksam_records.push({ ...record, effective_day_type: effectiveDayType });
          } else if (effectiveDayType === 'gece_mesaisi') {
            personnelDateGroups[key].gece_records.push({ ...record, effective_day_type: effectiveDayType });
          }
        }
      }
      
      // Şimdi her personel için gün ve saat hesapla
      for (const group of Object.values(personnelDateGroups)) {
        const pid = group.personnel_id;
        
        // Sadece aktif kayıtlar varsa hesapla
        if (group.records.length === 0 && group.cancelled_records.length === 0) continue;
        
        if (!summary[pid]) {
          // personnel_role'u bul
          const personData = personnel.find(p => p.id === pid);
          summary[pid] = {
            personnel_id: pid,
            personnel_name: group.personnel_name,
            personnel_role: personData?.role || '',
            cumartesi_count: 0,
            cumartesi_hours: 0,
            pazar_count: 0,
            pazar_hours: 0,
            resmi_tatil_count: 0,
            resmi_tatil_hours: 0,
            idari_tatil_count: 0,
            idari_tatil_hours: 0,
            aksam_mesaisi_count: 0,
            aksam_mesaisi_hours: 0,
            gece_mesaisi_count: 0,
            gece_mesaisi_hours: 0,
            total_days: 0,
            total_hours: 0,
            cancelled_count: 0, // İptal edilen gün sayısı
            cancelled_hours: 0  // İptal edilen toplam saat
          };
        }
        
        // Sadece aktif (iptal edilmemiş) kayıtlar için hesapla
        if (group.records.length > 0) {
          // NOT: Akşam/gece ile hafta sonu/tatil AYNI günde birlikte olabilir
          // (ör. Cumartesi gündüz park + akşam mesaisi). Eskiden `if akşam/gece
          // else gündüz` şeklinde AYRIK işleniyordu → gündüz saatleri sessizce
          // kayboluyordu. Artık her ikisi de EKLENEREK sayılır; gün (total_days)
          // yine takvim günü başına 1 kez artar.
          let dayCounted = false;

          // Akşam saatleri (varsa)
          if (group.aksam_records.length > 0) {
            const aksamHours = group.aksam_records.reduce((sum, r) => sum + (r.hours_worked || 0), 0);
            summary[pid].aksam_mesaisi_count += 1;
            summary[pid].aksam_mesaisi_hours += aksamHours;
            summary[pid].total_hours += aksamHours;
            dayCounted = true;
          }

          // Gece saatleri (varsa)
          if (group.gece_records.length > 0) {
            const geceHours = group.gece_records.reduce((sum, r) => sum + (r.hours_worked || 0), 0);
            summary[pid].gece_mesaisi_count += 1;
            summary[pid].gece_mesaisi_hours += geceHours;
            summary[pid].total_hours += geceHours;
            dayCounted = true;
          }

          // Akşam/gece DIŞINDAKİ gündüz kayıtları (cumartesi/pazar/resmi/idari) —
          // günlük tavan (yarım gün tatilde 4, normalde 8 saat) uygulanır.
          const dayRecords = group.records.filter(
            (r) => r.effective_day_type !== 'aksam_mesaisi' && r.effective_day_type !== 'gece_mesaisi'
          );
          if (dayRecords.length > 0) {
            const dayType = normalizeDayType(dayRecords[0].effective_day_type || '');
            // Yarım gün tatilde (arife) günlük tavan 4 saate iner.
            const isHalfDayHoliday =
              (dayType === 'resmi_tatil' || dayType === 'idari_tatil') &&
              halfDayDates.has(normDateKey(group.date));
            const dailyCap = isHalfDayHoliday ? 4 : 8;
            const dayTotalHours = Math.min(
              dayRecords.reduce((sum, r) => sum + (r.hours_worked || dailyCap), 0),
              dailyCap
            );

            // Gün sayısı: Bu tarih için sadece 1 gün say (kaç park olursa olsun)
            if (dayType === 'cumartesi') {
              summary[pid].cumartesi_count += 1;
              summary[pid].cumartesi_hours += dayTotalHours;
            } else if (dayType === 'pazar') {
              summary[pid].pazar_count += 1;
              summary[pid].pazar_hours += dayTotalHours;
            } else if (dayType === 'resmi_tatil') {
              summary[pid].resmi_tatil_count += 1;
              summary[pid].resmi_tatil_hours += dayTotalHours;
            } else if (dayType === 'idari_tatil') {
              summary[pid].idari_tatil_count += 1;
              summary[pid].idari_tatil_hours += dayTotalHours;
            }

            summary[pid].total_hours += dayTotalHours;
            dayCounted = true;
          }

          // Takvim günü sayısı: bu personel+tarih için yalnızca 1 kez.
          if (dayCounted) summary[pid].total_days += 1;
        }
        
        // İptal edilenleri ayrıca say (hesaplamaya dahil etme ama kaydet)
        if (group.cancelled_records.length > 0) {
          const cancelledCap = halfDayDates.has(normDateKey(group.date)) ? 4 : 8;
          const cancelledHours = Math.min(
            group.cancelled_records.reduce((sum, r) => sum + (r.hours_worked || cancelledCap), 0),
            cancelledCap
          );
          summary[pid].cancelled_count += 1;
          summary[pid].cancelled_hours += cancelledHours;
        }
      }
      
      const result = Object.values(summary).sort((a, b) => b.total_hours - a.total_hours);
      
      // İstatistikler
      let stats = { total_personnel: 0, average_days: 0, max_days: 0, min_days: 0, total_hours: 0, average_hours: 0, distribution_score: 100 };
      if (result.length > 0) {
        const totalDays = result.reduce((sum, p) => sum + p.total_days, 0);
        const totalHours = result.reduce((sum, p) => sum + p.total_hours, 0);
        stats = {
          total_personnel: result.length,
          average_days: (totalDays / result.length).toFixed(1),
          max_days: Math.max(...result.map(p => p.total_days)),
          min_days: Math.min(...result.map(p => p.total_days)),
          total_hours: totalHours,
          average_hours: (totalHours / result.length).toFixed(1),
          distribution_score: Math.round(100 - ((Math.max(...result.map(p => p.total_hours)) - Math.min(...result.map(p => p.total_hours))) / Math.max(totalHours / result.length, 1) * 50))
        };
      }
      
      setOvertimeSummary({ personnel_summary: result, statistics: stats });
    } catch (error) {
      console.error('Overtime summary fetch error:', error);
    }
  }, [reportStartDate, reportEndDate, holidays]);

  const fetchSuggestedPersonnel = useCallback(async (date) => {
    try {
      const response = await axios.get(`${API}/api/duty-schedules/suggest-personnel`, {
        params: { date }
      });
      setSuggestedPersonnel(response.data.available_personnel || []);
    } catch (error) {
      console.error('Suggested personnel fetch error:', error);
    }
  }, []);

  useEffect(() => {
    fetchParks();
    fetchPersonnel();
    fetchHolidays();
    fetchBadgeCounts(); // Badge sayılarını çek
  }, [fetchParks, fetchPersonnel, fetchHolidays, fetchBadgeCounts]);

  // Sekme görünür olduğunda badge ve veriyi yenile (çoklu panel: her sekme kendi verisini çeksin)
  useEffect(() => {
    const onVisible = () => {
      if (document.hidden) return;
      fetchBadgeCounts();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [fetchBadgeCounts]);

  // Mesai Anketi sekmesine her girişte anket listesini yenile (5+ panelde boş liste önlemi)
  useEffect(() => {
    if (activeSubTab !== 'survey') return;
    if (!document.hidden) window.dispatchEvent(new CustomEvent('refreshSurveys'));
  }, [activeSubTab]);

  // Mesai Takvimi sekmesine her girişte veriyi tazele.
  useEffect(() => {
    if (activeSubTab !== 'schedule') return;
    fetchHolidays();
    fetchDutySchedules();
  }, [activeSubTab, fetchHolidays, fetchDutySchedules]);

  // Takvim açıkken sekme tekrar görünür olduğunda yeniden senkronize et.
  useEffect(() => {
    if (activeSubTab !== 'schedule') return;

    const refreshCalendar = () => {
      if (document.hidden) return;
      fetchHolidays();
      fetchDutySchedules();
    };

    window.addEventListener('focus', refreshCalendar);
    document.addEventListener('visibilitychange', refreshCalendar);

    return () => {
      window.removeEventListener('focus', refreshCalendar);
      document.removeEventListener('visibilitychange', refreshCalendar);
    };
  }, [activeSubTab, fetchHolidays, fetchDutySchedules]);

  // Mesai raporu artık otomatik yüklenmiyor - "Rapor Getir" butonu tıklanmalı
  // useEffect kaldırıldı - kullanıcı tarih seçip butona basmalı

  // Week Navigation
  const goToPreviousWeek = () => setCurrentWeekStart(addDays(currentWeekStart, -7));
  const goToNextWeek = () => setCurrentWeekStart(addDays(currentWeekStart, 7));
  const goToCurrentWeek = () => setCurrentWeekStart(startOfWeek(new Date(), { weekStartsOn: 1 }));

  // Get week days
  const weekDays = eachDayOfInterval({
    start: currentWeekStart,
    end: endOfWeek(currentWeekStart, { weekStartsOn: 1 })
  });

  // Check if date is holiday
  const getHolidayForDate = (date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    const normalizeDateStr = (value) => String(value || '').slice(0, 10);
    return holidays.find(h => normalizeDateStr(h?.date) === dateStr);
  };

  // Get day type
  const getDayType = (date) => {
    const dayOfWeek = date.getDay();
    const holiday = getHolidayForDate(date);
    
    // Önce tatil kontrolü - tatil varsa döndür
    if (holiday) {
      // Backend iki ayrı konvansiyon kullanıyor: load-defaults "resmi"/"idari",
      // manuel ekleme "resmi_tatil"/"idari_tatil". Takvim render'ı ikincisini
      // beklediği için burada normalize ediyoruz.
      const rawType = String(holiday.holiday_type || 'resmi_tatil').toLowerCase();
      const normalizedType =
        rawType === 'resmi' ? 'resmi_tatil'
        : rawType === 'idari' ? 'idari_tatil'
        : rawType;
      return {
        type: normalizedType,
        name: holiday.name,
        description: holiday.description,
        holidayId: holiday.id,
        isFullDay: holiday.is_full_day !== false, // undefined/true → tam gün
        halfDayPeriod: holiday.half_day_period || null,
        created_by_name: holiday.created_by_name,
        created_by_role: holiday.created_by_role,
        updated_by_name: holiday.updated_by_name,
        updated_by_role: holiday.updated_by_role,
        isWeekend: dayOfWeek === 6 || dayOfWeek === 0 // Hafta sonu + tatil çakışması
      };
    }
    
    // Hafta sonu (tatil değil)
    if (dayOfWeek === 6) return { type: 'cumartesi', name: 'Cumartesi', isWeekend: true };
    if (dayOfWeek === 0) return { type: 'pazar', name: 'Pazar', isWeekend: true };
    
    return null;
  };

  const normalizeReportDayType = useCallback((value) => {
    const v = String(value || '').trim().toLowerCase();
    if (v === 'resmi') return 'resmi_tatil';
    if (v === 'idari') return 'idari_tatil';
    return v;
  }, []);

  const getEffectiveRecordDayType = useCallback((record) => {
    if (!record) return '';
    const holiday = holidays.find((h) => h?.date === record.date);
    const holidayType = normalizeReportDayType(holiday?.holiday_type);
    return holidayType || normalizeReportDayType(record.day_type);
  }, [holidays, normalizeReportDayType]);

  const getFilteredPersonnelDetailRecords = useCallback((personnelIdValue, filterType = 'all') => {
    let filtered = overtimeRecords
      .filter((r) => r.personnel_id === personnelIdValue)
      .map((r) => ({
        ...r,
        effective_day_type: getEffectiveRecordDayType(r)
      }));

    if (filterType !== 'all') {
      filtered = filtered.filter((r) => (r.effective_day_type || r.day_type) === filterType);
    }

    return filtered.sort((a, b) => String(b.date || '').localeCompare(String(a.date || '')));
  }, [overtimeRecords, getEffectiveRecordDayType]);

  // Check if day is special
  const isSpecialDay = (date) => getDayType(date) !== null;

  // Get schedules for date - akşam/gece mesailerini birleştir
  const getSchedulesForDate = (date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    const normalizeDateStr = (value) => String(value || '').slice(0, 10);
    const daySchedules = dutySchedules.filter(s => normalizeDateStr(s?.date) === dateStr);
    
    // Akşam ve gece mesailerini park_id'ye göre grupla
    const grouped = [];
    const aksamGeceGroups = {};
    
    daySchedules.forEach(schedule => {
      if (schedule.day_type === 'aksam_mesaisi' || schedule.day_type === 'gece_mesaisi') {
        const key = schedule.park_id || 'no-park';
        if (!aksamGeceGroups[key]) {
          aksamGeceGroups[key] = {
            aksam: null,
            gece: null,
            park_name: schedule.park_name,
            park_id: schedule.park_id
          };
        }
        if (schedule.day_type === 'aksam_mesaisi') {
          aksamGeceGroups[key].aksam = schedule;
        } else {
          aksamGeceGroups[key].gece = schedule;
        }
      } else {
        grouped.push(schedule);
      }
    });
    
    // Akşam/gece gruplarını birleşik schedule olarak ekle
    Object.values(aksamGeceGroups).forEach(group => {
      const aksam = group.aksam;
      const gece = group.gece;
      const baseSchedule = aksam || gece;
      
      if (baseSchedule) {
        // Tüm personeli birleştir
        const allPersonnel = [];
        const seenIds = new Set();
        
        [aksam, gece].forEach(s => {
          if (s?.assigned_personnel) {
            s.assigned_personnel.forEach(p => {
              if (!seenIds.has(p.personnel_id)) {
                seenIds.add(p.personnel_id);
                allPersonnel.push(p);
              }
            });
          }
        });
        
        // Toplam saat hesapla
        const aksamHours = aksam?.assigned_personnel?.[0]?.hours_worked || 0;
        const geceHours = gece?.assigned_personnel?.[0]?.hours_worked || 0;
        
        grouped.push({
          ...baseSchedule,
          id: baseSchedule.id,
          is_aksam_gece_combined: true,
          aksam_schedule: aksam,
          gece_schedule: gece,
          aksam_hours: aksamHours,
          gece_hours: geceHours,
          total_hours: aksamHours + geceHours,
          assigned_personnel: allPersonnel,
          display_time: aksam && gece 
            ? `${aksam.assigned_personnel?.[0]?.start_time || '17:00'}-${gece.assigned_personnel?.[0]?.end_time || '22:00'}`
            : aksam 
              ? `${aksam.assigned_personnel?.[0]?.start_time || '17:00'}-${aksam.assigned_personnel?.[0]?.end_time || '20:00'}`
              : `${gece.assigned_personnel?.[0]?.start_time || '20:00'}-${gece.assigned_personnel?.[0]?.end_time || '22:00'}`
        });
      }
    });
    
    return grouped;
  };

  // Handle Day Click
  const handleDayClick = (date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    setSelectedDate(date);
    setDialogMode('view');
    setHolidayName('');
    setHolidayType('idari_tatil');
    setHolidayDescription('');
    setHolidayIsFullDay(true);
    setHolidayHalfPeriod('afternoon');
    setHolidayEditId(null);
    setSelectedPark(null);
    setSelectedPersonnel([]);
    setShiftType('full_day');
    setStartTime('17:00');
    setEndTime('20:00');
    setPersonnelRoleFilter('all');
    fetchHolidayAuditForDate(dateStr);
    setShowDayDialog(true);

    // Dialog açılışını bloklamadan önerileri arkaplanda getir.
    fetchSuggestedPersonnel(dateStr).catch((error) => {
      console.error('Suggested personnel fetch (day click) error:', error);
    });
    
    // Temel roller için uyarı göster
    if (isBasicRole) {
      toast.info('Bu bölümü görüntüleme yetkiniz var, düzenleme yapamazsınız', { duration: 3000 });
    }
  };

  // Show Personnel Detail
  const handleShowPersonnelDetail = (person, filterType = 'all') => {
    setSelectedPersonnelDetail(person);
    setDetailFilterType(filterType);
    setSelectedPersonnelRecords(getFilteredPersonnelDetailRecords(person.personnel_id, filterType));
    setShowPersonnelDetailDialog(true);
  };

  // Handle Schedule Click - Tek bir mesai planının detayını göster
  const handleScheduleClick = async (schedule, date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    // Supervisor (Formen/BM) için özel dialog - sadece tıklanan kişiyi göster
    if (schedule.is_supervisor_schedule) {
      const supervisorRecord = {
        id: schedule.id,
        personnel_id: schedule.personnel_id,
        personnel_name: schedule.personnel_name,
        personnel_role: schedule.personnel_role,
        date: schedule.date,
        hours: schedule.hours || 8,
        start_time: schedule.start_time || '08:00',
        end_time: schedule.end_time || '17:00',
        day_type: schedule.day_type,
        cancelled: schedule.cancelled,
        cancelled_by_name: schedule.cancelled_by_name,
        cancel_reason: schedule.cancel_reason
      };
      
      setSupervisorDetailRecords([supervisorRecord]); // Sadece tıklanan kişi
      setSupervisorDetailDate(date);
      setSupervisorDetailRole(schedule.personnel_role); // Rol bilgisini kaydet
      setShowSupervisorDetailDialog(true);
      return;
    }
    
    // Detay kartını anında aç; ek veriler arkaplanda yüklensin.
    setSelectedScheduleDate(date);
    setSelectedScheduleDetail(schedule);
    fetchHolidayAuditForDate(dateStr);
    setShowScheduleDetailDialog(true);

    // Akşam/gece birleşik mesai için overtime_records'dan cancelled durumunu çek
    if (schedule.is_aksam_gece_combined) {
      try {
        const response = await axios.get(`${API}/api/overtime-records?month=${dateStr.substring(0, 7)}`);
        const records = response.data;
        
        // Bu tarih ve park için kayıtları filtrele
        const parkRecords = records.filter(r => 
          r.date === dateStr && 
          r.park_name === schedule.park_name &&
          (r.day_type === 'aksam_mesaisi' || r.day_type === 'gece_mesaisi')
        );
        
        // Akşam ve gece schedule'ları güncelle
        const updatedAksam = schedule.aksam_schedule ? { ...schedule.aksam_schedule } : null;
        const updatedGece = schedule.gece_schedule ? { ...schedule.gece_schedule } : null;
        
        if (updatedAksam?.assigned_personnel) {
          updatedAksam.assigned_personnel = updatedAksam.assigned_personnel.map(p => {
            const record = parkRecords.find(r => 
              r.personnel_id === p.personnel_id && r.day_type === 'aksam_mesaisi'
            );
            return {
              ...p,
              cancelled: record?.cancelled || false,
              cancel_reason: record?.cancel_reason,
              cancel_note: record?.cancel_note,
              cancelled_by_name: record?.cancelled_by_name,
              record_id: record?.id
            };
          });
        }
        
        if (updatedGece?.assigned_personnel) {
          updatedGece.assigned_personnel = updatedGece.assigned_personnel.map(p => {
            const record = parkRecords.find(r => 
              r.personnel_id === p.personnel_id && r.day_type === 'gece_mesaisi'
            );
            return {
              ...p,
              cancelled: record?.cancelled || false,
              cancel_reason: record?.cancel_reason,
              cancel_note: record?.cancel_note,
              cancelled_by_name: record?.cancelled_by_name,
              record_id: record?.id
            };
          });
        }
        
        // Güncellenmiş schedule'ı set et
        setSelectedScheduleDetail({
          ...schedule,
          aksam_schedule: updatedAksam,
          gece_schedule: updatedGece
        });
      } catch (error) {
        console.error('Error fetching overtime records:', error);
      }
    }
  };

  // Mesai kaydını iptal et (onaylanmış planlardan)
  const handleCancelOvertimeRecord = async () => {
    if (!cancelTargetRecord || !cancelReason) {
      toast.error('Lütfen iptal sebebini seçin');
      return;
    }

    try {
      setLoading(true);
      const auth = JSON.parse(localStorage.getItem('parkBakimAuth') || '{}');
      
      // Yeni endpoint'i kullan - personnel_id, date, park_name ve day_type ile kayıt bul
      const params = new URLSearchParams({
        personnel_id: cancelTargetRecord.personnel_id,
        date: cancelTargetRecord.date_raw, // YYYY-MM-DD formatında tarih
        park_name: cancelTargetRecord.park_name,
        cancelled_by: auth.personnelId,
        cancelled_by_name: auth.personnelName || '',
        cancel_reason: cancelReason,
        cancel_note: cancelNote || ''
      });
      
      // Akşam/gece mesaisi için day_type ekle
      if (cancelTargetRecord.day_type) {
        params.append('day_type', cancelTargetRecord.day_type);
      }
      
      await axios.put(`${API}/api/overtime-records/cancel-by-details?${params.toString()}`);

      toast.success(`${cancelTargetRecord.personnel_name} ${cancelTargetRecord.day_type === 'aksam_mesaisi' ? 'akşam' : cancelTargetRecord.day_type === 'gece_mesaisi' ? 'gece' : ''} mesaisi iptal edildi`);
      setShowCancelDialog(false);
      setShowScheduleDetailDialog(false); // Detay dialogunu da kapat
      setCancelTargetRecord(null);
      setCancelReason('');
      setCancelNote('');
      
      // Verileri yenile
      fetchDutySchedules();
      if (activeSubTab === 'reports') {
        fetchOvertimeSummary();
      }
    } catch (error) {
      toast.error('İptal işlemi başarısız: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  // İptal edilmiş mesaiyi geri al
  const handleRestoreOvertimeRecord = async (recordId) => {
    try {
      setLoading(true);
      await axios.put(`${API}/api/overtime-records/${recordId}/restore`);
      toast.success('Mesai kaydı geri alındı');
      
      // Verileri yenile
      fetchDutySchedules();
      if (activeSubTab === 'reports') {
        fetchOvertimeSummary();
      }
      
      // Eğer schedule detay açıksa, onu da yenile
      if (showScheduleDetailDialog && selectedScheduleDetail) {
        // Detay dialog'u kapat ve yeniden aç için schedule'ı güncelle
        const currentSchedule = selectedScheduleDetail;
        const currentDate = selectedScheduleDate;
        setShowScheduleDetailDialog(false);
        
        // Kısa bir gecikme ile tekrar aç (verilerin yenilenmesi için)
        setTimeout(async () => {
          // Akşam/gece birleşik mesai için güncel verileri çek
          if (currentSchedule.is_aksam_gece_combined) {
            try {
              const dateStr = format(currentDate, 'yyyy-MM-dd');
              const response = await axios.get(`${API}/api/overtime-records?month=${dateStr.substring(0, 7)}`);
              const records = response.data;
              
              const parkRecords = records.filter(r => 
                r.date === dateStr && 
                r.park_name === currentSchedule.park_name &&
                (r.day_type === 'aksam_mesaisi' || r.day_type === 'gece_mesaisi')
              );
              
              const updatedAksam = currentSchedule.aksam_schedule ? { ...currentSchedule.aksam_schedule } : null;
              const updatedGece = currentSchedule.gece_schedule ? { ...currentSchedule.gece_schedule } : null;
              
              if (updatedAksam?.assigned_personnel) {
                updatedAksam.assigned_personnel = updatedAksam.assigned_personnel.map(p => {
                  const record = parkRecords.find(r => 
                    r.personnel_id === p.personnel_id && r.day_type === 'aksam_mesaisi'
                  );
                  return {
                    ...p,
                    cancelled: record?.cancelled || false,
                    cancel_reason: record?.cancel_reason,
                    cancel_note: record?.cancel_note,
                    cancelled_by_name: record?.cancelled_by_name,
                    record_id: record?.id
                  };
                });
              }
              
              if (updatedGece?.assigned_personnel) {
                updatedGece.assigned_personnel = updatedGece.assigned_personnel.map(p => {
                  const record = parkRecords.find(r => 
                    r.personnel_id === p.personnel_id && r.day_type === 'gece_mesaisi'
                  );
                  return {
                    ...p,
                    cancelled: record?.cancelled || false,
                    cancel_reason: record?.cancel_reason,
                    cancel_note: record?.cancel_note,
                    cancelled_by_name: record?.cancelled_by_name,
                    record_id: record?.id
                  };
                });
              }
              
              setSelectedScheduleDetail({
                ...currentSchedule,
                aksam_schedule: updatedAksam,
                gece_schedule: updatedGece
              });
              setShowScheduleDetailDialog(true);
            } catch (error) {
              console.error('Error refreshing detail:', error);
            }
          }
        }, 300);
      }
    } catch (error) {
      toast.error('Geri alma işlemi başarısız: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  // İptal sebepleri
  const cancelReasons = [
    { value: 'hastalık', label: 'Hastalık' },
    { value: 'izin', label: 'İzin' },
    { value: 'aile_acil', label: 'Aile/Acil Durum' },
    { value: 'diger', label: 'Diğer' }
  ];

  // Mobil Ekip Oluştur
  const handleCreateMobileTeam = async () => {
    if (!mobileTeamDate) {
      toast.error('Tarih seçilmedi');
      return;
    }
    if (mobileTeamPersonnel.length === 0 && !formenJoining) {
      toast.error('En az bir personel seçin veya kendinizi ekleyin');
      return;
    }

    try {
      setLoading(true);
      const auth = JSON.parse(localStorage.getItem('parkBakimAuth') || '{}');
      const dateStr = format(mobileTeamDate, 'yyyy-MM-dd');
      const dayType = getDayType(mobileTeamDate);

      // Mobil saat ayarlarını kullan
      const startTimeValue = mobileTeamStartTime || '08:00';
      const endTimeValue = mobileTeamEndTime || '17:00';
      const hours = calculateHours(startTimeValue, endTimeValue);

      // Personel listesini hazırla
      let assignedPersonnel = mobileTeamPersonnel.map(p => ({
        personnel_id: p.id,
        personnel_name: p.name,
        personnel_role: p.role,
        shift_type: 'full_day',
        start_time: startTimeValue,
        end_time: endTimeValue,
        hours_worked: hours
      }));

      // Formen katılım durumu
      const formenId = isFormen ? personnelId : selectedMobileFormen;
      const formenData = formenId ? personnel.find(p => p.id === formenId) : null;
      
      if (formenData && formenJoining) {
        // Formen mesaiye katılıyorsa ekle (zaten eklenmemişse)
        if (!assignedPersonnel.find(p => p.personnel_id === formenId)) {
          assignedPersonnel.unshift({
            personnel_id: formenData.id,
            personnel_name: formenData.name,
            personnel_role: 'formen',
            shift_type: 'full_day',
            start_time: startTimeValue,
            end_time: endTimeValue,
            hours_worked: hours
          });
        }
      }

      // Bölge mühendisi katılım durumu
      const currentUserData = personnel.find(p => p.id === personnelId);
      
      // Bölge müh. - kendi girişi (bolge_muhendisi rolü)
      if (isBolgeMuhendisi && bolgeMuhJoining && currentUserData) {
        if (!assignedPersonnel.find(p => p.personnel_id === personnelId)) {
          assignedPersonnel.unshift({
            personnel_id: currentUserData.id,
            personnel_name: currentUserData.name,
            personnel_role: 'bolge_muhendisi',
            shift_type: 'full_day',
            start_time: startTimeValue,
            end_time: endTimeValue,
            hours_worked: hours
          });
        }
      }
      
      // Bölge müh. - müdür/müdür yrd. tarafından seçilmiş
      const selectedBolgeMuhData = selectedMobileBolgeMuh ? personnel.find(p => p.id === selectedMobileBolgeMuh) : null;
      if (['mudur', 'mudur_yardimcisi'].includes(userRole) && selectedBolgeMuhData && bolgeMuhJoining) {
        if (!assignedPersonnel.find(p => p.personnel_id === selectedMobileBolgeMuh)) {
          assignedPersonnel.unshift({
            personnel_id: selectedBolgeMuhData.id,
            personnel_name: selectedBolgeMuhData.name,
            personnel_role: 'bolge_muhendisi',
            shift_type: 'full_day',
            start_time: startTimeValue,
            end_time: endTimeValue,
            hours_worked: hours
          });
        }
      }

      // Bölge müh. ID ve name belirleme
      const bolgeMuhId = isBolgeMuhendisi ? personnelId : selectedMobileBolgeMuh;
      const bolgeMuhName = isBolgeMuhendisi ? currentUserData?.name : selectedBolgeMuhData?.name;

      // Duty schedule oluştur (schedule_type: mobile)
      const scheduleData = {
        date: dateStr,
        park_id: null,
        park_name: 'MOBİL EKİP',
        day_type: dayType?.type || 'hafta_ici',
        shift_type: 'full_day',
        schedule_type: 'mobile', // Mobil ekip
        note: mobileTeamNote || '',
        // Formen bilgisi
        formen_id: formenId,
        formen_name: formenData?.name,
        formen_joining: formenJoining,
        formen_not_joining_reason: !formenJoining ? formenNotJoiningReason : null,
        // Bölge mühendisi bilgisi
        bolge_muh_id: bolgeMuhId || null,
        bolge_muh_name: bolgeMuhName || null,
        bolge_muh_joining: bolgeMuhId ? bolgeMuhJoining : null,
        bolge_muh_not_joining_reason: bolgeMuhId && !bolgeMuhJoining ? bolgeMuhNotJoiningReason : null,
        assigned_personnel: assignedPersonnel,
        created_by: auth.personnelId,
        created_by_name: auth.personnelName
      };

      await axios.post(`${API}/api/duty-schedules`, scheduleData);

      const totalCount = assignedPersonnel.length;
      toast.success(`Mobil ekip mesaisi oluşturuldu (${totalCount} personel${formenJoining && formenData ? ', formen dahil' : ''}${bolgeMuhJoining && bolgeMuhId ? ', bölge müh. dahil' : ''})`);
      
      // Temizle ve kapat
      setShowMobileTeamDialog(false);
      setMobileTeamPersonnel([]);
      setMobileTeamNote('');
      setMobileTeamDate(null);
      setMobileTeamStartTime('08:00');
      setMobileTeamEndTime('17:00');
      setSelectedMobileFormen(null);
      setSelectedMobileBolgeMuh(null);
      setFormenJoining(true);
      setFormenNotJoiningReason('');
      setBolgeMuhJoining(false);
      setBolgeMuhNotJoiningReason('');
      
      // Verileri yenile
      fetchDutySchedules();
    } catch (error) {
      toast.error('Mobil ekip oluşturulamadı: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  // Mobil ekip personel toggle
  const toggleMobileTeamPersonnel = (person) => {
    setMobileTeamPersonnel(prev => {
      const exists = prev.find(p => p.id === person.id);
      if (exists) {
        return prev.filter(p => p.id !== person.id);
      } else {
        return [...prev, person];
      }
    });
  };

  // Create Holiday for Selected Date
  const handleCreateHolidayForDate = async () => {
    if (!holidayName.trim()) {
      toast.error('Lütfen tatil adını girin');
      return;
    }

    try {
      setLoading(true);
      const actor = getHolidayActorParams();
      await axios.post(`${API}/api/official-holidays`, {
        name: holidayName,
        date: format(selectedDate, 'yyyy-MM-dd'),
        holiday_type: holidayType,
        description: holidayDescription,
        is_recurring: false,
        is_full_day: holidayIsFullDay,
        half_day_period: holidayIsFullDay ? null : holidayHalfPeriod
      }, {
        params: {
          created_by: actor.actorId,
          created_by_name: actor.actorName,
          created_by_role: actor.actorRole
        }
      });

      toast.success(`${format(selectedDate, 'd MMMM yyyy', { locale: tr })} tarihi ${holidayEditId ? 'güncellendi' : 'tatil olarak eklendi'}`);
      setHolidayEditId(null);
      setDialogMode('view');
      fetchHolidays();
      fetchHolidayAuditForDate(format(selectedDate, 'yyyy-MM-dd'));
    } catch (error) {
      toast.error('Tatil eklenemedi: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  // Saat hesaplama
  const calculateHours = (start, end, includeLunchBreak = true) => {
    const [startH, startM] = start.split(':').map(Number);
    const [endH, endM] = end.split(':').map(Number);
    const startMinutes = startH * 60 + startM;
    const endMinutes = endH * 60 + endM;
    let totalMinutes = endMinutes - startMinutes;
    
    // Öğle arası (12:00-13:00) düşülmeli - tam gün mesailerde
    if (includeLunchBreak) {
      const lunchStart = 12 * 60; // 12:00
      const lunchEnd = 13 * 60;   // 13:00
      
      // Mesai öğle arasını kapsıyorsa 1 saat düş
      if (startMinutes < lunchEnd && endMinutes > lunchStart) {
        const overlapStart = Math.max(startMinutes, lunchStart);
        const overlapEnd = Math.min(endMinutes, lunchEnd);
        const lunchOverlap = overlapEnd - overlapStart;
        if (lunchOverlap > 0) {
          totalMinutes -= lunchOverlap;
        }
      }
    }
    
    return (totalMinutes / 60).toFixed(1);
  };

  // Overtime state'lerini sıfırla
  const resetOvertimeStates = () => {
    setOvertimeNote('');
    setOvertimeFormenJoining(false);
    setOvertimeFormenNotJoiningReason('');
    setOvertimeBolgeMuhJoining(false);
    setOvertimeBolgeMuhNotJoiningReason('');
    setSelectedOvertimeFormen(null);
    setSelectedOvertimeBolgeMuh(null);
    setSelectedPersonnel([]);
    setOvertimePersonnelSearch('');
    setOvertimeUseOther(false);
    setOvertimeOtherDescription('');
  };

  // Create Overtime for Normal Work Day (Akşam/Gece Mesaisi)
  const handleCreateOvertimeForDate = async () => {
    // Formen veya Bölge Müh. katılıyorsa veya personel seçilmişse devam et
    const hasParticipants = selectedPersonnel.length > 0 || overtimeFormenJoining || overtimeBolgeMuhJoining;
    
    // Park veya Diğer seçilmiş olmalı
    const hasLocation = selectedPark || overtimeUseOther;
    
    if (!hasLocation || !hasParticipants) {
      toast.error('Lütfen park/diğer seçin ve en az bir katılımcı belirleyin');
      return;
    }

    const park = selectedPark ? parks.find(p => p.id === selectedPark) : null;
    const parkName = overtimeUseOther ? (overtimeOtherDescription || 'Diğer Görev') : (park?.name || '');
    
    // Saat hesaplama helper
    const calcMinutes = (start, end) => {
      const [sh, sm] = start.split(':').map(Number);
      const [eh, em] = end.split(':').map(Number);
      return (eh * 60 + em) - (sh * 60 + sm);
    };
    
    const totalMinutes = calcMinutes(startTime, endTime);
    if (totalMinutes <= 0) {
      toast.error('Bitiş saati başlangıç saatinden sonra olmalı');
      return;
    }

    // Akşam/Gece ayrımı - 20:00 sınırı
    const nightBoundary = '20:00';
    const crossesBoundary = startTime < nightBoundary && endTime > nightBoundary;
    const isOnlyNight = startTime >= nightBoundary;
    const isOnlyEvening = endTime <= nightBoundary;

    try {
      setLoading(true);
      const auth = JSON.parse(localStorage.getItem('parkBakimAuth') || '{}');
      
      // Formen ve Bölge Müh. bilgileri
      const formenId = isFormen ? personnelId : selectedOvertimeFormen;
      const formenData = formenId ? personnel.find(p => p.id === formenId) : null;
      const bolgeMuhId = isBolgeMuhendisi ? personnelId : selectedOvertimeBolgeMuh;
      const bolgeMuhData = bolgeMuhId ? personnel.find(p => p.id === bolgeMuhId) : null;

      // Mesai oluşturma helper fonksiyonu
      const createShiftRecord = async (shiftStartTime, shiftEndTime, dayType) => {
        const shiftMinutes = calcMinutes(shiftStartTime, shiftEndTime);
        const shiftHours = parseFloat((shiftMinutes / 60).toFixed(1));
        
        // Personel listesini hazırla
        let assignedPersonnel = selectedPersonnel.map(pId => {
          const person = personnel.find(p => p.id === pId);
          return {
            personnel_id: pId,
            personnel_name: person?.name,
            personnel_role: person?.role,
            shift_type: 'hourly',
            start_time: shiftStartTime,
            end_time: shiftEndTime,
            hours_worked: shiftHours
          };
        });

        // Formen katılıyorsa ekle
        if (formenData && overtimeFormenJoining) {
          if (!assignedPersonnel.find(p => p.personnel_id === formenId)) {
            assignedPersonnel.unshift({
              personnel_id: formenData.id,
              personnel_name: formenData.name,
              personnel_role: 'formen',
              shift_type: 'hourly',
              start_time: shiftStartTime,
              end_time: shiftEndTime,
              hours_worked: shiftHours
            });
          }
        }

        // Bölge Müh. katılıyorsa ekle
        if (bolgeMuhData && overtimeBolgeMuhJoining) {
          if (!assignedPersonnel.find(p => p.personnel_id === bolgeMuhId)) {
            assignedPersonnel.unshift({
              personnel_id: bolgeMuhData.id,
              personnel_name: bolgeMuhData.name,
              personnel_role: 'bolge_muhendisi',
              shift_type: 'hourly',
              start_time: shiftStartTime,
              end_time: shiftEndTime,
              hours_worked: shiftHours
            });
          }
        }

        await axios.post(`${API}/api/duty-schedules`, {
          date: format(selectedDate, 'yyyy-MM-dd'),
          day_type: dayType,
          holiday_name: null,
          park_id: overtimeUseOther ? null : selectedPark,
          park_name: parkName,
          shift_type: 'hourly',
          note: overtimeNote || null,
          formen_id: formenId || null,
          formen_name: formenData?.name || null,
          formen_joining: formenId ? overtimeFormenJoining : null,
          formen_not_joining_reason: formenId && !overtimeFormenJoining ? overtimeFormenNotJoiningReason : null,
          bolge_muh_id: bolgeMuhId || null,
          bolge_muh_name: bolgeMuhData?.name || null,
          bolge_muh_joining: bolgeMuhId ? overtimeBolgeMuhJoining : null,
          bolge_muh_not_joining_reason: bolgeMuhId && !overtimeBolgeMuhJoining ? overtimeBolgeMuhNotJoiningReason : null,
          assigned_personnel: assignedPersonnel,
          created_by: personnelId || auth.personnelId || null,
          created_by_name: (personnel.find(p => p.id === personnelId)?.name) || auth.personnelName || null
        });
        
        return shiftHours;
      };

      // Mesaileri oluştur
      let eveningHours = 0;
      let nightHours = 0;
      
      if (isOnlyEvening) {
        // Sadece akşam mesaisi
        eveningHours = await createShiftRecord(startTime, endTime, 'aksam_mesaisi');
      } else if (isOnlyNight) {
        // Sadece gece mesaisi
        nightHours = await createShiftRecord(startTime, endTime, 'gece_mesaisi');
      } else if (crossesBoundary) {
        // Hem akşam hem gece - iki ayrı kayıt
        eveningHours = await createShiftRecord(startTime, nightBoundary, 'aksam_mesaisi');
        nightHours = await createShiftRecord(nightBoundary, endTime, 'gece_mesaisi');
      }

      // Başarı mesajı
      let successMsg = '';
      if (eveningHours > 0 && nightHours > 0) {
        successMsg = `Mesai oluşturuldu: ${eveningHours} saat akşam + ${nightHours} saat gece`;
      } else if (eveningHours > 0) {
        successMsg = `Akşam mesaisi oluşturuldu (${eveningHours} saat)`;
      } else {
        successMsg = `Gece mesaisi oluşturuldu (${nightHours} saat)`;
      }
      toast.success(successMsg);
      
      setDialogMode('view');
      setSelectedPark(null);
      resetOvertimeStates();
      fetchDutySchedules();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Mesai oluşturulamadı');
    } finally {
      setLoading(false);
    }
  };

  // Create Duty for Selected Date
  const handleCreateDutyForDate = async () => {
    if (!selectedPark || selectedPersonnel.length === 0) {
      toast.error('Lütfen park ve personel seçin');
      return;
    }

    const dayType = getDayType(selectedDate);
    const park = parks.find(p => p.id === selectedPark);

    if (!dayType) {
      toast.error('Bu gün için önce tatil tanımlamanız gerekiyor');
      return;
    }

    // Saatlik mesai için saat kontrolü
    if (shiftType === 'hourly') {
      const hours = parseFloat(calculateHours(startTime, endTime));
      if (hours <= 0) {
        toast.error('Bitiş saati başlangıç saatinden sonra olmalı');
        return;
      }
    }

    const hoursWorked = shiftType === 'full_day' ? 8 : parseFloat(calculateHours(startTime, endTime));

    try {
      setLoading(true);
      await axios.post(`${API}/api/duty-schedules`, {
        date: format(selectedDate, 'yyyy-MM-dd'),
        day_type: dayType.type,
        holiday_name: ['resmi_tatil', 'idari_tatil'].includes(dayType.type) ? dayType.name : null,
        park_id: selectedPark,
        park_name: park?.name || '',
        shift_type: shiftType,
        assigned_personnel: selectedPersonnel.map(pid => {
          const p = personnel.find(per => per.id === pid);
          return {
            personnel_id: pid,
            personnel_name: p?.name || '',
            personnel_role: p?.role || '',
            status: 'planned',
            shift_type: shiftType,
            start_time: shiftType === 'hourly' ? startTime : '08:00',
            end_time: shiftType === 'hourly' ? endTime : '17:00',
            hours_worked: hoursWorked
          };
        })
      });

      toast.success(`Mesai planı oluşturuldu (${shiftType === 'hourly' ? hoursWorked + ' saat' : 'Tam gün'})`);
      setDialogMode('view');
      setSelectedPark(null);
      setSelectedPersonnel([]);
      fetchDutySchedules();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Mesai planı oluşturulamadı');
    } finally {
      setLoading(false);
    }
  };

  // Silme onayı için dialog açma fonksiyonları
  const openDeleteConfirm = (type, id, name, e) => {
    if (e) e.stopPropagation();
    setDeleteType(type);
    setDeleteItemId(id);
    setDeleteItemName(name || '');
    setDeleteConfirmOpen(true);
  };

  // Onaylanan silme işlemi
  const handleConfirmedDelete = async () => {
    setDeleteInProgress(true);
    try {
      const actor = getHolidayActorParams();
      if (deleteType === 'duty') {
        await axios.delete(`${API}/api/duty-schedules/${deleteItemId}`);
        toast.success('Mesai planı silindi');
        fetchDutySchedules();
      } else if (deleteType === 'supervisor_overtime') {
        // Supervisor (Formen/BM) mesai kaydını iptal et
        const auth = JSON.parse(localStorage.getItem('parkBakimAuth') || '{}');
        await axios.put(`${API}/api/overtime-records/${deleteItemId}/cancel`, {
          cancelled_by: auth.personnelId,
          cancelled_by_name: auth.personnelName || '',
          cancel_reason: 'mazeretsiz',
          cancel_note: 'Takvimden iptal edildi'
        });
        toast.success(`${deleteItemName} mesaisi iptal edildi`);
        fetchDutySchedules();
      } else if (deleteType === 'holiday') {
        await axios.delete(`${API}/api/official-holidays/${deleteItemId}`, {
          params: {
            deleted_by: actor.actorId,
            deleted_by_name: actor.actorName,
            deleted_by_role: actor.actorRole
          },
          timeout: 15000
        });
        toast.success('Tatil silindi');
        fetchHolidays();
      } else if (deleteType === 'holidayFromDay') {
        await axios.delete(`${API}/api/official-holidays/${deleteItemId}`, {
          params: {
            deleted_by: actor.actorId,
            deleted_by_name: actor.actorName,
            deleted_by_role: actor.actorRole
          },
          timeout: 15000
        });
        toast.success('Tatil kaldırıldı');
        fetchHolidays();
        if (selectedDate) {
          fetchHolidayAuditForDate(format(selectedDate, 'yyyy-MM-dd'));
        }
        setShowDayDialog(false);
      } else if (deleteType === 'holidayAudit') {
        await axios.delete(`${API}/api/official-holidays-audit/${deleteItemId}`, {
          params: {
            deleted_by: actor.actorId,
            deleted_by_name: actor.actorName,
            deleted_by_role: actor.actorRole
          }
        });
        toast.success('Tatil işlem geçmişi kaydı silindi');
        if (selectedDate) {
          fetchHolidayAuditForDate(format(selectedDate, 'yyyy-MM-dd'));
        }
      }
    } catch (error) {
      toast.error('Silme işlemi başarısız: ' + (error.response?.data?.detail || error.message));
    } finally {
      setDeleteInProgress(false);
      setDeleteConfirmOpen(false);
      setDeleteType(null);
      setDeleteItemId(null);
      setDeleteItemName('');
    }
  };

  // Holiday Tab Functions
  const handleCreateHoliday = async () => {
    if (!newHolidayName.trim() || !newHolidayDate) {
      toast.error('Lütfen tatil adı ve tarihini girin');
      return;
    }

    try {
      setLoading(true);
      const actor = getHolidayActorParams();
      await axios.post(`${API}/api/official-holidays`, {
        name: newHolidayName.trim(),
        date: newHolidayDate,
        holiday_type: newHolidayType,
        is_recurring: newHolidayRecurring,
        is_full_day: newHolidayIsFullDay,
        half_day_period: newHolidayIsFullDay ? null : newHolidayHalfPeriod
      }, {
        params: {
          created_by: actor.actorId,
          created_by_name: actor.actorName,
          created_by_role: actor.actorRole
        }
      });

      toast.success('Tatil eklendi');
      setShowAddHolidayDialog(false);
      setNewHolidayName('');
      setNewHolidayDate('');
      setNewHolidayIsFullDay(true);
      setNewHolidayHalfPeriod('afternoon');
      fetchHolidays();
    } catch (error) {
      toast.error('Tatil eklenemedi: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  const handleLoadDefaultHolidays = async () => {
    try {
      setLoading(true);
      const year = new Date().getFullYear();
      const response = await axios.post(`${API}/api/official-holidays/load-defaults?year=${year}`, null, { timeout: 20000 });
      toast.success(response.data.message);
      fetchHolidays();
    } catch (error) {
      const msg = error.code === 'ECONNABORTED' ? 'İstek zaman aşımına uğradı. Tekrar deneyin.' : (error.response?.data?.detail || 'Varsayılan tatiller yüklenemedi');
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  // Park Priority Functions
  const handleUpdateParkPriority = async (parkId, priorityLevel) => {
    try {
      await axios.put(`${API}/api/parks/${parkId}/priority`, null, {
        params: {
          priority_level: priorityLevel,
          weekend_duty_required: ['cok_yogun', 'yogun'].includes(priorityLevel),
          holiday_duty_required: ['cok_yogun', 'yogun'].includes(priorityLevel)
        }
      });
      toast.success('Park önceliği güncellendi');
      fetchParks();
    } catch (error) {
      toast.error('Park önceliği güncellenemedi');
    }
  };

  const togglePersonnelSelection = (personnelId) => {
    setSelectedPersonnel(prev => 
      prev.includes(personnelId) 
        ? prev.filter(id => id !== personnelId)
        : [...prev, personnelId]
    );
  };

  // Get day background color
  const getDayBackgroundClass = (date) => {
    const dayType = getDayType(date);
    if (!dayType) return 'bg-white border-gray-200 hover:border-blue-400 hover:bg-blue-50';

    // Yarım gün tatiller (arife) — tam tatille karışmasın diye amber tonlu kesik kenar.
    if (dayType.holidayId && dayType.isFullDay === false) {
      return 'bg-amber-50 border-amber-300 border-dashed hover:bg-amber-100';
    }

    if (dayType.type === 'resmi_tatil') return 'bg-red-50 border-red-300 hover:bg-red-100';
    if (dayType.type === 'idari_tatil') return 'bg-purple-50 border-purple-300 hover:bg-purple-100';
    if (dayType.type === 'cumartesi' || dayType.type === 'pazar') return 'bg-blue-50 border-blue-300 hover:bg-blue-100';

    return 'bg-white border-gray-200';
  };

  // Filtered and sorted parks - ALFABETİK SIRALAMA + ÖNCELİK FİLTRESİ
  const filteredParks = parks
    .filter(park => {
      // Arama filtresi
      const matchesSearch = normalizeSearchText(park.name).includes(normalizeSearchText(parkSearchTerm)) ||
        (park.neighborhood && normalizeSearchText(park.neighborhood).includes(normalizeSearchText(parkSearchTerm)));
      
      // Öncelik filtresi
      const matchesPriority = !selectedPriorityFilter || (park.priority_level || 'normal') === selectedPriorityFilter;
      
      return matchesSearch && matchesPriority;
    })
    .sort((a, b) => a.name.localeCompare(b.name, 'tr'));

  // Öncelik bazlı park sayıları
  const priorityCounts = {
    cok_yogun: parks.filter(p => p.priority_level === 'cok_yogun').length,
    yogun: parks.filter(p => p.priority_level === 'yogun').length,
    normal: parks.filter(p => !p.priority_level || p.priority_level === 'normal').length,
    az_yogun: parks.filter(p => p.priority_level === 'az_yogun').length
  };

  // Filtrelenmiş personel listesi (rol bazlı)
  const filteredPersonnel = personnel
    .filter(p => personnelRoleFilter === 'all' || p.role === personnelRoleFilter)
    .filter(p => overtimePersonnelSearch.trim() ? normalizeSearchText(p.name).includes(normalizeSearchText(overtimePersonnelSearch)) : true)
    .sort((a, b) => a.name.localeCompare(b.name, 'tr'));

  // Clickable number component
  const ClickableNumber = ({ value, hours, onClick, colorClass = '' }) => {
    if (value === 0) return <span className="text-gray-400">0</span>;
    return (
      <button 
        onClick={onClick}
        className={`font-semibold hover:underline cursor-pointer ${colorClass}`}
        title={hours ? `${hours} saat` : ''}
      >
        {value}
        {hours > 0 && <span className="text-xs ml-1 opacity-70">({hours}s)</span>}
      </button>
    );
  };

  return (
    <div className="space-y-4" data-testid="mesai-yonetimi-tab">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <CalendarClock className="h-8 w-8 text-blue-600" />
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Mesai Yönetimi</h2>
            <p className="text-sm text-gray-500">Hafta sonu ve resmi/idari tatil mesai planlaması</p>
          </div>
        </div>
        {/* SSE Bağlantı Durumu */}
        <div className="flex items-center gap-2">
          {sseConnected ? (
            <div className="flex items-center gap-1 text-green-600 text-xs" title="Gerçek zamanlı güncellemeler aktif">
              <Wifi className="h-4 w-4" />
              <span className="hidden sm:inline">Canlı</span>
            </div>
          ) : (
            <button 
              onClick={sseReconnect}
              className="flex items-center gap-1 text-gray-400 text-xs hover:text-gray-600" 
              title="Bağlantı yok - Yeniden bağlan"
            >
              <WifiOff className="h-4 w-4" />
              <span className="hidden sm:inline">Bağlan</span>
            </button>
          )}
        </div>
      </div>

      <Tabs value={activeSubTab} onValueChange={setActiveSubTab}>
        <TabsList className={`grid w-full ${isBasicRole ? 'grid-cols-5' : 'grid-cols-6'}`}>
          <TabsTrigger value="survey" data-testid="tab-survey" className="relative">
            <Users className="h-4 w-4 mr-2" />
            Mesai Anketi
            {activeSurveysBadge > 0 && (
              <span className={`absolute -top-1 -right-1 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center ${
                isMudur ? 'bg-gray-400' : 'bg-red-500'
              }`}>
                {activeSurveysBadge}
              </span>
            )}
          </TabsTrigger>
          <TabsTrigger value="plans" data-testid="tab-plans" className="relative">
            <CalendarClock className="h-4 w-4 mr-2" />
            Taslak Planlar
            {pendingPlansBadge > 0 && (
              <span className={`absolute -top-1 -right-1 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center ${
                isMudur ? 'bg-gray-400' : 'bg-red-500'
              }`}>
                {pendingPlansBadge}
              </span>
            )}
          </TabsTrigger>
          <TabsTrigger value="schedule" data-testid="tab-schedule">
            <CalendarIcon className="h-4 w-4 mr-2" />
            Mesai Takvimi
          </TabsTrigger>
          <TabsTrigger value="holidays" data-testid="tab-holidays">
            <CalendarClock className="h-4 w-4 mr-2" />
            Resmi Tatiller
          </TabsTrigger>
          {/* Park Öncelikleri - Sadece amir rolleri için */}
          {!isBasicRole && (
            <TabsTrigger value="parks" data-testid="tab-parks">
              <TreePine className="h-4 w-4 mr-2" />
              Park Öncelikleri
            </TabsTrigger>
          )}
          {/* Mesai Raporu - Tüm roller görür ama temel roller sadece kendi verilerini */}
          <TabsTrigger value="reports" data-testid="tab-reports">
            <BarChart3 className="h-4 w-4 mr-2" />
            {isBasicRole ? 'Mesai Bilgilerim' : 'Mesai Raporu'}
          </TabsTrigger>
        </TabsList>

        {/* Mesai Anketi Tab */}
        <TabsContent value="survey" className="space-y-4">
          <MesaiAnketiSection 
            userRole={userRole} 
            personnel={personnel}
            allPersonnel={allPersonnel}
            parks={parks}
            personnelId={personnelId}
            onCreatePlanFromSurvey={(planData) => {
              // Plan verilerini kaydet ve Taslak Planlar sekmesine geç
              // Dialog TaslakPlanlarSection içinde açılacak
              setPlanFromSurvey(planData);
              setActiveSubTab('plans'); // Plans tab'ına geç - dialog orada açılacak
            }}
          />
        </TabsContent>

        {/* Taslak Planlar Tab */}
        <TabsContent value="plans" className="space-y-4">
          <TaslakPlanlarSection 
            userRole={userRole}
            personnel={personnel}
            parks={parks}
            holidays={holidays}
            planFromSurvey={planFromSurvey}
            onPlanFromSurveyHandled={() => setPlanFromSurvey(null)}
            onPlanCreated={(surveyId) => {
              // Plan oluşturulduktan sonra anket sonuçlarına dön
              setActiveSubTab('survey');
              // Anket sonuçları modalını aç
              setTimeout(() => {
                // MesaiAnketiSection'daki selectedSurvey'i set etmek için event yayınla
                window.dispatchEvent(new CustomEvent('openSurveyResults', { detail: { surveyId } }));
              }, 100);
            }}
          />
        </TabsContent>

        {/* Mesai Takvimi Tab */}
        <TabsContent value="schedule" className="space-y-4">
          <MesaiTakvimiSection
            currentWeekStart={currentWeekStart}
            weekDays={weekDays}
            isBasicRole={isBasicRole}
            canEditSchedule={canMutateMesai}
            goToPreviousWeek={goToPreviousWeek}
            goToNextWeek={goToNextWeek}
            goToCurrentWeek={goToCurrentWeek}
            getDayType={getDayType}
            getSchedulesForDate={getSchedulesForDate}
            isSpecialDay={isSpecialDay}
            getDayBackgroundClass={getDayBackgroundClass}
            handleDayClick={handleDayClick}
            handleScheduleClick={handleScheduleClick}
            openDeleteConfirm={openDeleteConfirm}
          />
        </TabsContent>

        {/* Resmi Tatiller Tab */}
        <TabsContent value="holidays" className="space-y-4">
          <ResmiTatillerSection
            holidays={holidays}
            isBasicRole={isBasicRole}
            canEditHolidays={canMutateMesai}
            loading={loading}
            onLoadDefaultHolidays={handleLoadDefaultHolidays}
            onAddHolidayClick={() => setShowAddHolidayDialog(true)}
            onDeleteHoliday={openDeleteConfirm}
          />
        </TabsContent>

        {/* Park Öncelikleri Tab - Sadece amir rolleri için görünür (gölge admin sadece görüntüleme) */}
        <TabsContent value="parks" className="space-y-4">
          <ParkOncelikleriSection
            parkSearchTerm={parkSearchTerm}
            setParkSearchTerm={setParkSearchTerm}
            selectedPriorityFilter={selectedPriorityFilter}
            setSelectedPriorityFilter={setSelectedPriorityFilter}
            parks={parks}
            filteredParks={filteredParks}
            priorityCounts={priorityCounts}
            onUpdateParkPriority={canMutateMesai ? handleUpdateParkPriority : undefined}
          />
        </TabsContent>


        {/* Mesai Raporu Tab */}
        <TabsContent value="reports" className="space-y-4">
          <MesaiRaporuSection
            reportStartDate={reportStartDate}
            setReportStartDate={setReportStartDate}
            reportEndDate={reportEndDate}
            setReportEndDate={setReportEndDate}
            reportPersonnelSearch={reportPersonnelSearch}
            setReportPersonnelSearch={setReportPersonnelSearch}
            reportRoleFilter={reportRoleFilter}
            setReportRoleFilter={setReportRoleFilter}
            reportSortBy={reportSortBy}
            setReportSortBy={setReportSortBy}
            overtimeSummary={overtimeSummary}
            personnel={personnel}
            allPersonnelForCount={allPersonnel}
            isBasicRole={isBasicRole}
            userRole={userRole}
            fetchOvertimeSummary={fetchOvertimeSummary}
            onPersonnelDetailClick={handleShowPersonnelDetail}
            personnelId={personnelId}
          />
        </TabsContent>
      </Tabs>

      {/* Day Click Dialog - SÜRÜKLENEBİLİR MODAL */}
      <DraggableModal 
        isOpen={showDayDialog} 
        onClose={() => setShowDayDialog(false)}
        title={selectedDate && format(selectedDate, 'd MMMM yyyy, EEEE', { locale: tr })}
        maxWidth="max-w-2xl"
      >
        {selectedDate && (
          <div className="space-y-4">
            {/* Current Day Status */}
            {getDayType(selectedDate) ? (
              <div className={`p-3 rounded-lg ${
                getDayType(selectedDate).type === 'resmi_tatil' ? 'bg-red-50 border border-red-200' :
                getDayType(selectedDate).type === 'idari_tatil' ? 'bg-purple-50 border border-purple-200' :
                'bg-blue-50 border border-blue-200'
              }`}>
                <div className="flex items-center justify-between">
                  <div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <Badge className={
                        getDayType(selectedDate).type === 'resmi_tatil' ? 'bg-red-200 text-red-800' :
                        getDayType(selectedDate).type === 'idari_tatil' ? 'bg-purple-200 text-purple-800' :
                        'bg-blue-200 text-blue-800'
                      }>
                        {getDayType(selectedDate).type === 'resmi_tatil' ? 'Resmi Tatil' :
                         getDayType(selectedDate).type === 'idari_tatil' ? 'İdari Tatil' :
                         dayTypeLabels[getDayType(selectedDate).type]}
                      </Badge>
                      {getDayType(selectedDate).holidayId && getDayType(selectedDate).isFullDay === false && (
                        <Badge className="bg-amber-200 text-amber-800">
                          ½ Yarım Gün{getDayType(selectedDate).halfDayPeriod === 'morning' ? ' (Sabah Tatil)' : ' (Öğleden Sonra Tatil)'}
                        </Badge>
                      )}
                    </div>
                    <p className="font-semibold mt-1">{getDayType(selectedDate).name}</p>
                    {(getDayType(selectedDate).created_by_name || getDayType(selectedDate).updated_by_name) && (
                      <p className="text-xs text-gray-600 mt-1">
                        {getDayType(selectedDate).updated_by_name
                          ? `${getRoleTitle(getDayType(selectedDate).updated_by_role) || getDayType(selectedDate).updated_by_role || 'Yetkili'} ${getDayType(selectedDate).updated_by_name} tarafından güncellendi`
                          : `${getRoleTitle(getDayType(selectedDate).created_by_role) || getDayType(selectedDate).created_by_role || 'Yetkili'} ${getDayType(selectedDate).created_by_name} tarafından oluşturuldu`
                        }
                      </p>
                    )}
                    {getDayType(selectedDate).description && (
                      <p className="text-sm text-gray-600">{getDayType(selectedDate).description}</p>
                    )}
                  </div>
                  {getDayType(selectedDate).holidayId && canMutateMesai && (
                    <div className="flex flex-col gap-2 shrink-0">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        const dt = getDayType(selectedDate);
                        setHolidayEditId(dt.holidayId);
                        setHolidayType(dt.type === 'idari_tatil' ? 'idari_tatil' : 'resmi_tatil');
                        setHolidayName(dt.name || '');
                        setHolidayDescription(dt.description || '');
                        setHolidayIsFullDay(dt.isFullDay !== false);
                        setHolidayHalfPeriod(dt.halfDayPeriod || 'afternoon');
                        setDialogMode('add_holiday');
                      }}
                    >
                      <CalendarClock className="h-4 w-4 mr-1" />
                      Düzenle
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="text-red-600 hover:bg-red-50"
                      onClick={() => openDeleteConfirm('holidayFromDay', getDayType(selectedDate).holidayId, getDayType(selectedDate).name)}
                    >
                      <Trash2 className="h-4 w-4 mr-1" />
                      Tatili Kaldır
                    </Button>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="p-3 bg-gray-50 rounded-lg border">
                <p className="text-gray-600">Normal iş günü - Tatil tanımlayabilir veya akşam saatlik mesai ekleyebilirsiniz</p>
              </div>
            )}

            {holidayAudit.length > 0 && (
              <div className="p-3 rounded-lg border bg-gray-50">
                <p className="text-sm font-semibold mb-2">Tatil İşlem Geçmişi</p>
                <div className="space-y-1 max-h-28 overflow-y-auto">
                  {holidayAudit.map((entry) => (
                    <div key={entry.id} className="flex items-center justify-between gap-2">
                      <p className="text-xs text-gray-600">
                        {entry.action === 'created' ? '➕ Eklendi' : entry.action === 'updated' ? '✏️ Güncellendi' : '🗑️ Kaldırıldı'}:
                        {' '}{entry.name || '-'} ({entry.holiday_type === 'idari_tatil' ? 'İdari' : 'Resmi'})
                        {' '} - {entry.by_name || 'Bilinmeyen'}
                        {' '}({getRoleTitle(entry.by_role) || entry.by_role || 'rol yok'})
                      </p>
                      {isMudur && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 w-6 p-0 text-red-600 hover:bg-red-100"
                          onClick={() => openDeleteConfirm('holidayAudit', entry.id, entry.name || 'İşlem kaydı')}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Mode Selector - sadece amir rolleri için */}
            {dialogMode === 'view' && canMutateMesai && (
              <div className="flex gap-2 flex-wrap">
                {/* Normal iş günü (Pazartesi-Cuma, tatil değil) */}
                {!getDayType(selectedDate) && (
                  <>
                    <Button 
                      variant="outline" 
                      onClick={() => setDialogMode('add_holiday')}
                      className="flex-1"
                    >
                      <CalendarClock className="h-4 w-4 mr-2" />
                      Tatil Olarak İşaretle
                    </Button>
                    <Button 
                      onClick={() => {
                        setShiftType('hourly');
                        setStartTime('17:00');
                        setEndTime('20:00');
                        setDialogMode('add_overtime');
                      }}
                      className="flex-1 bg-orange-600 hover:bg-orange-700"
                    >
                      <Clock className="h-4 w-4 mr-2" />
                      Akşam/Gece Mesaisi Ekle
                    </Button>
                  </>
                )}
                {/* Hafta sonu (Cumartesi/Pazar) - Tatil ekleme seçeneği de olmalı (resmi tatil hafta sonuna denk gelebilir) */}
                {getDayType(selectedDate) && ['cumartesi', 'pazar'].includes(getDayType(selectedDate).type) && !getDayType(selectedDate).holidayId && (
                  <>
                    <Button 
                      variant="outline" 
                      onClick={() => setDialogMode('add_holiday')}
                      className="flex-1"
                    >
                      <CalendarClock className="h-4 w-4 mr-2" />
                      Resmi Tatil Olarak İşaretle
                    </Button>
                    <Button 
                      onClick={() => setDialogMode('add_duty')}
                      className="flex-1"
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Mesai Ekle
                    </Button>
                  </>
                )}
                {/* Resmi/İdari tatil veya hafta sonu ile tatil çakışması */}
                {getDayType(selectedDate) && !['cumartesi', 'pazar'].includes(getDayType(selectedDate).type) && (
                  <Button 
                    onClick={() => setDialogMode('add_duty')}
                    className="flex-1"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Mesai Ekle
                  </Button>
                )}
                {/* Hafta sonu + resmi tatil olarak işaretlenmiş */}
                {getDayType(selectedDate) && getDayType(selectedDate).holidayId && ['cumartesi', 'pazar'].includes(getDayType(selectedDate).type) && (
                  <Button 
                    onClick={() => setDialogMode('add_duty')}
                    className="flex-1"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Mesai Ekle
                  </Button>
                )}
                
                {/* Mobil Ekip Butonu - Tüm gün tiplerinde görünür */}
                <Button 
                  onClick={() => {
                    setMobileTeamDate(selectedDate);
                    setMobileTeamPersonnel([]);
                    setMobileTeamNote('');
                    setShowDayDialog(false);
                    setShowMobileTeamDialog(true);
                  }}
                  className="flex-1 bg-purple-600 hover:bg-purple-700"
                >
                  <Users className="h-4 w-4 mr-2" />
                  Mobil Ekip Ekle
                </Button>
              </div>
            )}

            {/* Add Holiday Form */}
            {dialogMode === 'add_holiday' && (
              <div className="space-y-4 p-4 border rounded-lg bg-gray-50">
                <h4 className="font-semibold">{holidayEditId ? 'Tatili Düzenle' : 'Tatil Tanımla'}</h4>

                <div className="space-y-2">
                  <Label>Tatil Türü</Label>
                  <SearchableSelect
                    value={holidayType}
                    onChange={setHolidayType}
                    showAllOption={false}
                    placeholder="Tatil türü seçin..."
                    searchPlaceholder="Tür ara..."
                    options={[
                      { id: 'resmi_tatil', name: '🔴 Resmi Tatil' },
                      { id: 'idari_tatil', name: '🟣 İdari Tatil' },
                    ]}
                  />
                </div>

                <div className="space-y-2">
                  <Label>Gün Kapsamı</Label>
                  <SearchableSelect
                    value={holidayIsFullDay ? 'full' : 'half'}
                    onChange={(v) => setHolidayIsFullDay(v === 'full')}
                    showAllOption={false}
                    placeholder="Gün kapsamı seçin..."
                    searchPlaceholder="Ara..."
                    options={[
                      { id: 'full', name: 'Tam Gün' },
                      { id: 'half', name: 'Yarım Gün' },
                    ]}
                  />
                </div>

                {!holidayIsFullDay && (
                  <div className="space-y-2">
                    <Label>Tatil Olan Yarı</Label>
                    <SearchableSelect
                      value={holidayHalfPeriod}
                      onChange={setHolidayHalfPeriod}
                      showAllOption={false}
                      placeholder="Yarı seçin..."
                      searchPlaceholder="Ara..."
                      options={[
                        { id: 'afternoon', name: 'Öğleden Sonra Tatil (Sabah Mesai)' },
                        { id: 'morning', name: 'Sabah Tatil (Öğleden Sonra Mesai)' },
                      ]}
                    />
                  </div>
                )}

                <div className="space-y-2">
                  <Label>Tatil Adı</Label>
                  <Input
                    value={holidayName}
                    onChange={(e) => setHolidayName(e.target.value)}
                    placeholder="Örn: Ramazan Bayramı Arifesi, İdari İzin"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Açıklama (Opsiyonel)</Label>
                  <Textarea
                    value={holidayDescription}
                    onChange={(e) => setHolidayDescription(e.target.value)}
                    placeholder="Örn: Bakanlar Kurulu kararıyla ilan edildi"
                    rows={2}
                  />
                </div>

                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => { setHolidayEditId(null); setDialogMode('view'); }} className="flex-1">
                    İptal
                  </Button>
                  <Button onClick={handleCreateHolidayForDate} disabled={loading} className="flex-1">
                    {loading ? 'Kaydediliyor...' : (holidayEditId ? 'Değişiklikleri Kaydet' : 'Tatil Olarak Kaydet')}
                  </Button>
                </div>
              </div>
            )}

            {/* Add Overtime Form - AKŞAM/GECE MESAİSİ */}
            {dialogMode === 'add_overtime' && (() => {
              // Akşam/Gece ayrımı hesapla
              const nightBoundary = '20:00';
              const crossesBoundary = startTime < nightBoundary && endTime > nightBoundary;
              const isOnlyNight = startTime >= nightBoundary;
              const isOnlyEvening = endTime <= nightBoundary;
              
              // Saat hesaplamaları
              const calcMinutes = (start, end) => {
                const [sh, sm] = start.split(':').map(Number);
                const [eh, em] = end.split(':').map(Number);
                return (eh * 60 + em) - (sh * 60 + sm);
              };
              
              let eveningMinutes = 0;
              let nightMinutes = 0;
              
              if (isOnlyEvening) {
                eveningMinutes = calcMinutes(startTime, endTime);
              } else if (isOnlyNight) {
                nightMinutes = calcMinutes(startTime, endTime);
              } else if (crossesBoundary) {
                eveningMinutes = calcMinutes(startTime, nightBoundary);
                nightMinutes = calcMinutes(nightBoundary, endTime);
              }
              
              const eveningHours = (eveningMinutes / 60).toFixed(1);
              const nightHours = (nightMinutes / 60).toFixed(1);
              const totalHours = ((eveningMinutes + nightMinutes) / 60).toFixed(1);
              
              return (
              <div className={`space-y-4 p-4 border rounded-lg ${crossesBoundary ? 'bg-gradient-to-r from-orange-50 to-indigo-50 border-gray-300' : isOnlyNight ? 'bg-indigo-50 border-indigo-200' : 'bg-orange-50 border-orange-200'}`}>
                <h4 className="font-semibold text-gray-800">
                  🌙 Akşam/Gece Mesaisi Ekle
                </h4>
                <p className="text-sm text-gray-600">
                  17:00-20:00 = Akşam Mesaisi | 20:00 sonrası = Gece Mesaisi
                </p>
                
                {/* Saat Seçimi */}
                <div className="p-3 bg-white rounded-lg border border-gray-200">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Başlangıç Saati</Label>
                      <Input type="time" value={startTime} onChange={(e) => setStartTime(e.target.value)} />
                    </div>
                    <div className="space-y-2">
                      <Label>Bitiş Saati</Label>
                      <Input type="time" value={endTime} onChange={(e) => setEndTime(e.target.value)} />
                    </div>
                  </div>
                  
                  {/* Mesai Dağılımı Gösterimi */}
                  <div className="mt-3 p-2 bg-gray-50 rounded border">
                    <div className="text-sm font-medium text-gray-700 mb-2">Mesai Dağılımı:</div>
                    <div className="flex flex-wrap gap-2">
                      {eveningMinutes > 0 && (
                        <Badge className="bg-orange-500 text-white">
                          🌙 Akşam: {eveningHours} saat
                        </Badge>
                      )}
                      {nightMinutes > 0 && (
                        <Badge className="bg-indigo-600 text-white">
                          🌃 Gece: {nightHours} saat
                        </Badge>
                      )}
                      <Badge variant="outline" className="font-bold">
                        Toplam: {totalHours} saat
                      </Badge>
                    </div>
                    {crossesBoundary && (
                      <p className="text-xs text-gray-500 mt-2">
                        ℹ️ Mesai 20:00'da bölünerek ayrı kaydedilecek
                      </p>
                    )}
                  </div>
                </div>

                {/* Bölge Mühendisi Katılım - Bölge Müh. için */}
                {isBolgeMuhendisi && (
                  <div className="p-3 bg-cyan-50 rounded-lg border border-cyan-200 space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-gray-700">🔵 Ben de mesaiye katılacağım</span>
                      <div className="flex items-center gap-3">
                        <label className="flex items-center gap-1 cursor-pointer">
                          <input type="radio" checked={overtimeBolgeMuhJoining} onChange={() => { setOvertimeBolgeMuhJoining(true); setOvertimeBolgeMuhNotJoiningReason(''); }} className="w-4 h-4" />
                          <span className="text-sm text-green-700">✓ Evet</span>
                        </label>
                        <label className="flex items-center gap-1 cursor-pointer">
                          <input type="radio" checked={!overtimeBolgeMuhJoining} onChange={() => setOvertimeBolgeMuhJoining(false)} className="w-4 h-4" />
                          <span className="text-sm text-gray-700">✗ Hayır</span>
                        </label>
                      </div>
                    </div>
                    {!overtimeBolgeMuhJoining && (
                      <Input placeholder="Katılmama sebebi (opsiyonel)..." value={overtimeBolgeMuhNotJoiningReason} onChange={(e) => setOvertimeBolgeMuhNotJoiningReason(e.target.value)} className="text-sm" />
                    )}
                  </div>
                )}

                {/* Müdür Yardımcısı / Müdür için Bölge Müh. ve Formen Seçimi */}
                {['mudur_yardimcisi', 'mudur'].includes(userRole) && (
                  <>
                    {/* Bölge Mühendisi Seçimi */}
                    <div className="p-3 bg-cyan-50 rounded-lg border border-cyan-200 space-y-2">
                      <Label className="text-cyan-700">🔵 Bölge Mühendisi (Opsiyonel)</Label>
                      <SearchableSelect
                        value={selectedOvertimeBolgeMuh || 'none'}
                        onChange={(val) => { setSelectedOvertimeBolgeMuh(val === 'none' ? null : val); setOvertimeBolgeMuhJoining(val !== 'none'); }}
                        allValue="none"
                        allLabel="Seçilmedi"
                        searchPlaceholder="Bölge mühendisi ara..."
                        options={personnel.filter(p => p.role === 'bolge_muhendisi').filter(p => isMudur || p.mudur_yardimcisi_id === personnelId).sort((a, b) => a.name.localeCompare(b.name, 'tr')).map(bm => ({ id: bm.id, name: bm.name }))}
                      />
                      {selectedOvertimeBolgeMuh && (
                        <div className="flex items-center justify-between pt-2">
                          <span className="text-sm">{personnel.find(p => p.id === selectedOvertimeBolgeMuh)?.name} katılacak mı?</span>
                          <div className="flex gap-2">
                            <label className="flex items-center gap-1 cursor-pointer">
                              <input type="radio" checked={overtimeBolgeMuhJoining} onChange={() => { setOvertimeBolgeMuhJoining(true); setOvertimeBolgeMuhNotJoiningReason(''); }} className="w-4 h-4" />
                              <span className="text-sm text-green-700">Evet</span>
                            </label>
                            <label className="flex items-center gap-1 cursor-pointer">
                              <input type="radio" checked={!overtimeBolgeMuhJoining} onChange={() => setOvertimeBolgeMuhJoining(false)} className="w-4 h-4" />
                              <span className="text-sm text-red-700">Hayır</span>
                            </label>
                          </div>
                        </div>
                      )}
                      {selectedOvertimeBolgeMuh && !overtimeBolgeMuhJoining && (
                        <Input placeholder="Katılmama sebebi (opsiyonel)..." value={overtimeBolgeMuhNotJoiningReason} onChange={(e) => setOvertimeBolgeMuhNotJoiningReason(e.target.value)} className="text-sm" />
                      )}
                    </div>

                    {/* Formen Seçimi */}
                    <div className="p-3 bg-blue-50 rounded-lg border border-blue-200 space-y-2">
                      <Label className="text-blue-700">🟢 Formen (Opsiyonel)</Label>
                      <SearchableSelect
                        value={selectedOvertimeFormen || 'none'}
                        onChange={(val) => { setSelectedOvertimeFormen(val === 'none' ? null : val); setOvertimeFormenJoining(val !== 'none'); }}
                        allValue="none"
                        allLabel="Seçilmedi"
                        searchPlaceholder="Formen ara..."
                        options={personnel.filter(p => p.role === 'formen').filter(p => {
                          if (isMudur) return true;
                          if (isMudurYardimcisi) return p.mudur_yardimcisi_id === personnelId;
                          return false;
                        }).sort((a, b) => a.name.localeCompare(b.name, 'tr')).map(f => ({ id: f.id, name: f.name }))}
                      />
                      {selectedOvertimeFormen && (
                        <div className="flex items-center justify-between pt-2">
                          <span className="text-sm">{personnel.find(p => p.id === selectedOvertimeFormen)?.name} katılacak mı?</span>
                          <div className="flex gap-2">
                            <label className="flex items-center gap-1 cursor-pointer">
                              <input type="radio" checked={overtimeFormenJoining} onChange={() => { setOvertimeFormenJoining(true); setOvertimeFormenNotJoiningReason(''); }} className="w-4 h-4" />
                              <span className="text-sm text-green-700">Evet</span>
                            </label>
                            <label className="flex items-center gap-1 cursor-pointer">
                              <input type="radio" checked={!overtimeFormenJoining} onChange={() => setOvertimeFormenJoining(false)} className="w-4 h-4" />
                              <span className="text-sm text-red-700">Hayır</span>
                            </label>
                          </div>
                        </div>
                      )}
                      {selectedOvertimeFormen && !overtimeFormenJoining && (
                        <Input placeholder="Katılmama sebebi (opsiyonel)..." value={overtimeFormenNotJoiningReason} onChange={(e) => setOvertimeFormenNotJoiningReason(e.target.value)} className="text-sm" />
                      )}
                    </div>
                  </>
                )}

                {/* Formen için kendi katılım seçeneği */}
                {isFormen && (
                  <div className="p-3 bg-green-50 rounded-lg border border-green-200 space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-gray-700">🟢 Ben de mesaiye katılacağım</span>
                      <div className="flex items-center gap-3">
                        <label className="flex items-center gap-1 cursor-pointer">
                          <input type="radio" checked={overtimeFormenJoining} onChange={() => { setOvertimeFormenJoining(true); setOvertimeFormenNotJoiningReason(''); }} className="w-4 h-4" />
                          <span className="text-sm text-green-700">✓ Evet</span>
                        </label>
                        <label className="flex items-center gap-1 cursor-pointer">
                          <input type="radio" checked={!overtimeFormenJoining} onChange={() => setOvertimeFormenJoining(false)} className="w-4 h-4" />
                          <span className="text-sm text-gray-700">✗ Hayır</span>
                        </label>
                      </div>
                    </div>
                    {!overtimeFormenJoining && (
                      <Input placeholder="Katılmama sebebi (opsiyonel)..." value={overtimeFormenNotJoiningReason} onChange={(e) => setOvertimeFormenNotJoiningReason(e.target.value)} className="text-sm" />
                    )}
                  </div>
                )}
                
                {/* Park Seçimi veya Diğer */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label>Konum Seçin</Label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <Checkbox 
                        checked={overtimeUseOther} 
                        onCheckedChange={(checked) => {
                          setOvertimeUseOther(checked);
                          if (checked) setSelectedPark(null);
                        }} 
                      />
                      <span className="text-sm text-gray-600">Diğer (Park dışı görev)</span>
                    </label>
                  </div>
                  
                  {!overtimeUseOther ? (
                    <SearchableSelect
                      value={selectedPark || ''}
                      onChange={setSelectedPark}
                      showAllOption={false}
                      placeholder="Park seçin..."
                      searchPlaceholder="Park ara..."
                      options={parks
                        .filter(park => {
                          // Bölge mühendisi/formen seçimine göre filtrele
                          if (selectedOvertimeFormen || selectedOvertimeBolgeMuh) {
                            const targetId = selectedOvertimeFormen || selectedOvertimeBolgeMuh;
                            const targetPerson = personnel.find(p => p.id === targetId);
                            if (targetPerson?.region_id) {
                              return park.region_id === targetPerson.region_id;
                            }
                          }
                          // Giriş yapan kullanıcının bölgesine göre filtrele
                          const currentUser = personnel.find(p => p.id === personnelId);
                          if (currentUser?.region_id) {
                            return park.region_id === currentUser.region_id;
                          }
                          return true;
                        })
                        .sort((a, b) => a.name.localeCompare(b.name, 'tr'))
                        .map(park => ({ id: park.id, name: park.name }))}
                    />
                  ) : (
                    <Input 
                      placeholder="Görev açıklaması (ör: Sulama tesisatı, Depo işleri...)" 
                      value={overtimeOtherDescription}
                      onChange={(e) => setOvertimeOtherDescription(e.target.value)}
                    />
                  )}
                </div>

                {/* Personel Seçimi - Arama ve Hiyerarşik */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label>Personel Seçin ({selectedPersonnel.length} seçili)</Label>
                    <SearchableSelect
                      value={personnelRoleFilter}
                      onChange={setPersonnelRoleFilter}
                      className="w-36 h-8"
                      allValue="all"
                      allLabel="Tüm Roller"
                      searchPlaceholder="Rol ara..."
                      options={[
                        { id: 'park_gorevlisi', name: 'Park Görevlisi' },
                        { id: 'çim_ekibi', name: 'Çim Ekibi' },
                      ]}
                    />
                  </div>

                  {/* Seçilenler özeti — kimleri seçtiğin net görünsün (kaldırılabilir) */}
                  {selectedPersonnel.length > 0 && (
                    <div className="flex flex-wrap items-center gap-1.5 p-2 rounded-lg bg-indigo-50 border border-indigo-200">
                      <span className="text-xs font-medium text-indigo-700 mr-1">Seçilenler:</span>
                      {selectedPersonnel.map(pId => {
                        const person = personnel.find(p => p.id === pId);
                        if (!person) return null;
                        return (
                          <span
                            key={pId}
                            className="inline-flex items-center gap-1 bg-white border border-indigo-300 text-indigo-800 rounded-full pl-2 pr-1 py-0.5 text-xs"
                          >
                            <span className="truncate max-w-[140px]">{person.name}</span>
                            <button
                              type="button"
                              onClick={() => togglePersonnelSelection(pId)}
                              className="flex items-center justify-center w-4 h-4 rounded-full text-indigo-500 hover:bg-indigo-200 hover:text-indigo-900"
                              title="Listeden çıkar"
                            >
                              ×
                            </button>
                          </span>
                        );
                      })}
                      <button
                        type="button"
                        onClick={() => setSelectedPersonnel([])}
                        className="ml-auto text-xs text-gray-500 hover:text-red-600 underline"
                      >
                        Tümünü kaldır
                      </button>
                    </div>
                  )}

                  {/* Personel Arama */}
                  <Input 
                    placeholder="İsim ile ara..." 
                    value={overtimePersonnelSearch}
                    onChange={(e) => setOvertimePersonnelSearch(e.target.value)}
                    className="h-8"
                  />
                  
                  <div className="border rounded-lg p-2 max-h-40 overflow-y-auto space-y-1 bg-white">
                    {personnel
                      .filter(p => ['park_gorevlisi', 'çim_ekibi'].includes(p.role))
                      .filter(p => personnelRoleFilter === 'all' || p.role === personnelRoleFilter)
                      .filter(p => {
                        // İsim ile arama
                        if (overtimePersonnelSearch.trim()) {
                          return normalizeSearchText(p.name).includes(normalizeSearchText(overtimePersonnelSearch));
                        }
                        return true;
                      })
                      .filter(p => {
                        // Seçilen formen/bölge mühendisine göre filtrele
                        const targetFormenId = selectedOvertimeFormen || (isFormen ? personnelId : null);
                        const targetBmId = selectedOvertimeBolgeMuh || (isBolgeMuhendisi ? personnelId : null);
                        
                        if (targetFormenId) {
                          return p.formen_id === targetFormenId;
                        }
                        if (targetBmId) {
                          const formen = personnel.find(f => f.id === p.formen_id);
                          return formen?.bolge_muhendisi_id === targetBmId;
                        }
                        if (isMudurYardimcisi) {
                          const formen = personnel.find(f => f.id === p.formen_id);
                          if (formen?.mudur_yardimcisi_id === personnelId) return true;
                          const bm = personnel.find(b => b.id === formen?.bolge_muhendisi_id);
                          return bm?.mudur_yardimcisi_id === personnelId;
                        }
                        if (isMudur) return true;
                        return false;
                      })
                      .sort((a, b) => a.name.localeCompare(b.name, 'tr'))
                      .map(person => (
                        <div 
                          key={person.id}
                          className={`flex items-center justify-between p-1.5 rounded cursor-pointer text-sm ${
                            selectedPersonnel.includes(person.id) 
                              ? endTime >= '20:00' ? 'bg-indigo-100 border border-indigo-300' : 'bg-orange-100 border border-orange-300'
                              : 'hover:bg-gray-50'
                          }`}
                          onClick={() => togglePersonnelSelection(person.id)}
                        >
                          <div className="flex items-center gap-2">
                            <span className="truncate">{person.name}</span>
                            <Badge variant="outline" className="text-[10px] px-1">
                              {roleLabels[person.role] || person.role}
                            </Badge>
                          </div>
                          <Checkbox checked={selectedPersonnel.includes(person.id)} />
                        </div>
                      ))}
                    {personnel
                      .filter(p => ['park_gorevlisi', 'çim_ekibi'].includes(p.role))
                      .filter(p => personnelRoleFilter === 'all' || p.role === personnelRoleFilter)
                      .filter(p => overtimePersonnelSearch.trim() ? normalizeSearchText(p.name).includes(normalizeSearchText(overtimePersonnelSearch)) : true)
                      .length === 0 && (
                        <div className="text-center text-gray-400 text-sm py-2">Personel bulunamadı</div>
                      )}
                  </div>
                </div>

                {/* Opsiyonel Açıklama */}
                <div className="space-y-2">
                  <Label>Açıklama (Opsiyonel)</Label>
                  <Input 
                    placeholder="Örn: Sulama işleri, budama vb..." 
                    value={overtimeNote} 
                    onChange={(e) => setOvertimeNote(e.target.value)} 
                  />
                </div>

                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => { setDialogMode('view'); resetOvertimeStates(); }} className="flex-1">
                    İptal
                  </Button>
                  <Button 
                    onClick={handleCreateOvertimeForDate} 
                    disabled={loading} 
                    className="flex-1 bg-gradient-to-r from-orange-500 to-indigo-600 hover:from-orange-600 hover:to-indigo-700"
                  >
                    {loading ? 'Oluşturuluyor...' : 'Mesai Oluştur'}
                  </Button>
                </div>
              </div>
            );
            })()}

            {/* Add Duty Form - SAATLİK MESAİ DESTEĞİ */}
            {dialogMode === 'add_duty' && (
              <div className="space-y-4 p-4 border rounded-lg bg-gray-50">
                <h4 className="font-semibold">Mesai Planı Oluştur</h4>
                
                {/* Mesai Türü Seçimi */}
                <div className="space-y-2">
                  <Label>Mesai Türü</Label>
                  <div className="flex gap-4">
                    <label className={`flex items-center gap-2 p-3 rounded-lg border-2 cursor-pointer transition-all ${
                      shiftType === 'full_day' ? 'border-green-500 bg-green-50' : 'border-gray-200 hover:border-gray-300'
                    }`}>
                      <input 
                        type="radio" 
                        name="shiftType" 
                        value="full_day"
                        checked={shiftType === 'full_day'}
                        onChange={() => setShiftType('full_day')}
                        className="sr-only"
                      />
                      <CalendarIcon className={`h-5 w-5 ${shiftType === 'full_day' ? 'text-green-600' : 'text-gray-400'}`} />
                      <div>
                        <div className={`font-medium ${shiftType === 'full_day' ? 'text-green-700' : 'text-gray-700'}`}>
                          Tam Gün
                        </div>
                        <div className="text-xs text-gray-500">08:00 - 17:00 (8 saat)</div>
                      </div>
                    </label>
                    
                    <label className={`flex items-center gap-2 p-3 rounded-lg border-2 cursor-pointer transition-all ${
                      shiftType === 'hourly' ? 'border-orange-500 bg-orange-50' : 'border-gray-200 hover:border-gray-300'
                    }`}>
                      <input 
                        type="radio" 
                        name="shiftType" 
                        value="hourly"
                        checked={shiftType === 'hourly'}
                        onChange={() => setShiftType('hourly')}
                        className="sr-only"
                      />
                      <Clock className={`h-5 w-5 ${shiftType === 'hourly' ? 'text-orange-600' : 'text-gray-400'}`} />
                      <div>
                        <div className={`font-medium ${shiftType === 'hourly' ? 'text-orange-700' : 'text-gray-700'}`}>
                          Saatlik Mesai
                        </div>
                        <div className="text-xs text-gray-500">Özel saat aralığı</div>
                      </div>
                    </label>
                  </div>
                </div>

                {/* Saatlik Mesai için Saat Seçimi */}
                {shiftType === 'hourly' && (
                  <div className="p-3 bg-orange-50 rounded-lg border border-orange-200">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Başlangıç Saati</Label>
                        <Input type="time" value={startTime} onChange={(e) => setStartTime(e.target.value)} />
                      </div>
                      <div className="space-y-2">
                        <Label>Bitiş Saati</Label>
                        <Input type="time" value={endTime} onChange={(e) => setEndTime(e.target.value)} />
                      </div>
                    </div>
                    <div className="mt-2 text-sm text-orange-700">
                      <Clock className="h-4 w-4 inline mr-1" />
                      Toplam: <strong>{calculateHours(startTime, endTime)} saat</strong>
                    </div>
                  </div>
                )}
                
                <div className="space-y-2">
                  <Label>Park Seçin</Label>
                  <SearchableSelect
                    value={selectedPark || ''}
                    onChange={setSelectedPark}
                    showAllOption={false}
                    placeholder="Park seçin..."
                    searchPlaceholder="Park ara..."
                    options={[
                      ...parks
                        .filter(p => p.weekend_duty_required || p.holiday_duty_required)
                        .slice()
                        .sort((a, b) => a.name.localeCompare(b.name, 'tr'))
                        .map(park => ({
                          id: park.id,
                          name: `${priorityColors[park.priority_level]?.label || '🟢'} ${park.name}`,
                          group: "Mesai Gerektiren Parklar",
                        })),
                      ...parks
                        .slice()
                        .sort((a, b) => a.name.localeCompare(b.name, 'tr'))
                        .map(park => ({ id: park.id, name: park.name, group: "Tüm Parklar" })),
                    ]}
                  />
                </div>

                {/* Personel Seçimi - ROL FİLTRESİYLE */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label>Personel Seçin ({selectedPersonnel.length} seçili)</Label>
                    <SearchableSelect
                      value={personnelRoleFilter}
                      onChange={setPersonnelRoleFilter}
                      className="w-48 h-8"
                      allValue="all"
                      allLabel="Tüm Roller"
                      searchPlaceholder="Rol ara..."
                      options={mesaiRoles.map(role => ({ id: role, name: roleLabels[role] }))}
                    />
                  </div>

                  {/* İsim ile arama */}
                  <Input
                    placeholder="İsim ile ara..."
                    value={overtimePersonnelSearch}
                    onChange={(e) => setOvertimePersonnelSearch(e.target.value)}
                    className="h-8"
                  />

                  <div className="border rounded-lg p-3 max-h-48 overflow-y-auto space-y-2 bg-white">
                    {suggestedPersonnel.filter(person => overtimePersonnelSearch.trim() ? normalizeSearchText(person.personnel_name).includes(normalizeSearchText(overtimePersonnelSearch)) : true).length > 0 && (
                      <div className="mb-3 pb-3 border-b">
                        <p className="text-xs font-semibold text-green-600 mb-2">
                          ⭐ Önerilen (Az mesai yapmış)
                        </p>
                        {suggestedPersonnel.filter(person => overtimePersonnelSearch.trim() ? normalizeSearchText(person.personnel_name).includes(normalizeSearchText(overtimePersonnelSearch)) : true).slice(0, 5).map(person => (
                          <div 
                            key={person.personnel_id}
                            className={`flex items-center justify-between p-2 rounded cursor-pointer ${
                              selectedPersonnel.includes(person.personnel_id) 
                                ? 'bg-green-100 border border-green-300' 
                                : 'bg-gray-50 hover:bg-gray-100'
                            }`}
                            onClick={() => togglePersonnelSelection(person.personnel_id)}
                          >
                            <div>
                              <span className="font-medium">{person.personnel_name}</span>
                              <span className="text-xs text-gray-500 ml-2">
                                ({person.recent_duty_count} mesai)
                              </span>
                            </div>
                            <Checkbox checked={selectedPersonnel.includes(person.personnel_id)} />
                          </div>
                        ))}
                      </div>
                    )}
                    
                    {filteredPersonnel.map(person => (
                      <div 
                        key={person.id}
                        className={`flex items-center justify-between p-2 rounded cursor-pointer ${
                          selectedPersonnel.includes(person.id) 
                            ? 'bg-blue-100 border border-blue-300' 
                            : 'hover:bg-gray-50'
                        }`}
                        onClick={() => togglePersonnelSelection(person.id)}
                      >
                        <div className="flex items-center gap-2">
                          <span>{person.name}</span>
                          <Badge variant="outline" className="text-xs">
                            {roleLabels[person.role] || person.role}
                          </Badge>
                        </div>
                        <Checkbox checked={selectedPersonnel.includes(person.id)} />
                      </div>
                    ))}
                  </div>
                </div>

                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => setDialogMode('view')} className="flex-1">
                    İptal
                  </Button>
                  <Button onClick={handleCreateDutyForDate} disabled={loading} className="flex-1">
                    {loading ? 'Oluşturuluyor...' : `Mesai Oluştur ${shiftType === 'hourly' ? `(${calculateHours(startTime, endTime)} saat)` : '(Tam Gün)'}`}
                  </Button>
                </div>
              </div>
            )}

            {/* Existing Schedules - Kompakt Liste */}
            {getSchedulesForDate(selectedDate).length > 0 && (
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <h4 className="font-semibold">Mevcut Mesai Planları</h4>
                  <Badge variant="secondary">{getSchedulesForDate(selectedDate).length} mesai</Badge>
                </div>
                <div className="max-h-64 overflow-y-auto space-y-2 pr-1">
                  {getSchedulesForDate(selectedDate).map(schedule => (
                    <div key={schedule.id} className={`p-2 border rounded-lg ${
                      schedule.cancelled 
                        ? 'bg-red-50/50 border-red-200 opacity-70'
                        : schedule.is_supervisor_schedule 
                          ? schedule.personnel_role === 'formen' 
                            ? 'bg-violet-50 border-violet-200' 
                            : 'bg-cyan-50 border-cyan-200'
                          : schedule.schedule_type === 'mobile' 
                            ? 'bg-purple-50 border-purple-200' 
                            : 'bg-white'
                    }`}>
                      <div className="flex items-center justify-between">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className={`font-semibold text-sm truncate ${
                              schedule.cancelled ? 'line-through text-gray-400' :
                              schedule.is_supervisor_schedule
                                ? schedule.personnel_role === 'formen' ? 'text-violet-700' : 'text-cyan-700'
                                : schedule.schedule_type === 'mobile' ? 'text-purple-700' : 'text-green-700'
                            }`}>
                              {schedule.is_supervisor_schedule && (schedule.personnel_role === 'formen' ? '👷 ' : '🔍 ')}
                              {schedule.schedule_type === 'mobile' && '🚐 '}
                              {schedule.is_supervisor_schedule ? schedule.personnel_name : schedule.park_name}
                            </span>
                            {schedule.cancelled && (
                              <Badge variant="outline" className="text-[10px] bg-red-100 text-red-600 border-red-300">
                                İPTAL
                              </Badge>
                            )}
                            {!schedule.cancelled && schedule.is_supervisor_schedule ? (
                              <Badge variant="outline" className={`text-[10px] shrink-0 ${
                                schedule.personnel_role === 'formen' ? 'bg-violet-100 text-violet-700' : 'bg-cyan-100 text-cyan-700'
                              }`}>
                                {schedule.personnel_role === 'formen' ? 'Alan Denetimi' : 'Bölge Denetimi'}
                              </Badge>
                            ) : !schedule.cancelled && !schedule.is_supervisor_schedule && (
                              <Badge variant="outline" className="text-[10px] shrink-0">
                                {schedule.assigned_personnel?.filter(p => !p.cancelled).length || 0} kişi
                              </Badge>
                            )}
                          </div>
                          {/* Supervisor için saat ve süre bilgisi */}
                          {schedule.is_supervisor_schedule && (
                            <div className={`text-[11px] mt-1 flex items-center gap-2 ${schedule.cancelled ? 'text-gray-400 line-through' : 'text-gray-600'}`}>
                              <Clock className="h-3 w-3" />
                              <span>{schedule.start_time || '08:00'} - {schedule.end_time || '17:00'}</span>
                              <span className="text-gray-400">|</span>
                              <span className="font-medium">{schedule.hours || 8} saat</span>
                            </div>
                          )}
                          {/* İptal bilgisi */}
                          {schedule.cancelled && schedule.cancelled_by_name && (
                            <div className="text-[10px] text-red-500 mt-1">
                              İptal eden: {schedule.cancelled_by_name}
                              {schedule.cancel_reason && ` (${
                                schedule.cancel_reason === 'hastalık' ? 'Hastalık' :
                                schedule.cancel_reason === 'izin' ? 'İzin' :
                                schedule.cancel_reason === 'aile_acil' ? 'Aile Acil' : 'Diğer'
                              })`}
                            </div>
                          )}
                          {/* Formen/Bölge Müh bilgisi - tek satır */}
                          {schedule.schedule_type === 'mobile' && (
                            <div className="text-[11px] text-gray-500 mt-1 truncate">
                              {schedule.formen_name && (
                                <span className={schedule.formen_joining === false ? 'text-orange-600' : 'text-green-600'}>
                                  👤 {schedule.formen_name} {schedule.formen_joining === false && '(Katılmıyor)'}
                                </span>
                              )}
                              {schedule.bolge_muh_name && (
                                <span className={`ml-2 ${schedule.bolge_muh_joining === false ? 'text-orange-600' : 'text-cyan-600'}`}>
                                  🔵 {schedule.bolge_muh_name}
                                </span>
                              )}
                            </div>
                          )}
                          {schedule.created_by_name && !schedule.is_supervisor_schedule && (
                            <div className="text-[10px] text-gray-400 truncate">
                              Oluşturan: {schedule.created_by_name}
                            </div>
                          )}
                          {schedule.note && (
                            <div className="text-[11px] text-amber-700 mt-1 flex items-start gap-1">
                              <span className="shrink-0">📝</span>
                              <span className="italic break-words">{schedule.note}</span>
                            </div>
                          )}
                          {!schedule.is_supervisor_schedule && schedule.formen_name && schedule.formen_joining === false && (
                            <div className="text-[11px] text-orange-600 mt-1 flex items-start gap-1">
                              <span className="shrink-0">👤</span>
                              <span className="break-words">{schedule.formen_name} katılmadı{schedule.formen_not_joining_reason ? ` (${schedule.formen_not_joining_reason})` : ''}</span>
                            </div>
                          )}
                        </div>
                        <div className="flex items-center gap-1 shrink-0 ml-2">
                          {/* Sadece Detay butonu - İptal/Geri Al işlemleri detay içinde yapılacak */}
                          <Button 
                            variant="outline" 
                            size="sm"
                            className={`text-xs h-7 px-2 ${schedule.is_supervisor_schedule 
                              ? schedule.personnel_role === 'formen' 
                                ? 'border-violet-300 text-violet-700 hover:bg-violet-50' 
                                : 'border-cyan-300 text-cyan-700 hover:bg-cyan-50'
                              : ''
                            }`}
                            onClick={(e) => {
                              e.stopPropagation();
                              setShowDayDialog(false);
                              handleScheduleClick(schedule, selectedDate);
                            }}
                          >
                            <Eye className="h-3 w-3 mr-1" />
                            Detay
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </DraggableModal>

      {/* Add Holiday Dialog - Ayrı Bileşen */}
      <AddHolidayDialog
        isOpen={showAddHolidayDialog}
        onClose={() => setShowAddHolidayDialog(false)}
        holidayType={newHolidayType}
        setHolidayType={setNewHolidayType}
        holidayName={newHolidayName}
        setHolidayName={setNewHolidayName}
        holidayDate={newHolidayDate}
        setHolidayDate={setNewHolidayDate}
        holidayRecurring={newHolidayRecurring}
        setHolidayRecurring={setNewHolidayRecurring}
        holidayIsFullDay={newHolidayIsFullDay}
        setHolidayIsFullDay={setNewHolidayIsFullDay}
        holidayHalfPeriod={newHolidayHalfPeriod}
        setHolidayHalfPeriod={setNewHolidayHalfPeriod}
        onSubmit={handleCreateHoliday}
        loading={loading}
      />

      {/* Personnel Detail Dialog - SÜRÜKLENEBİLİR */}
      <DraggableModal
        isOpen={showPersonnelDetailDialog}
        onClose={() => setShowPersonnelDetailDialog(false)}
        title={`${selectedPersonnelDetail?.personnel_name} - Mesai Detayları`}
        maxWidth="max-w-3xl"
      >
        {selectedPersonnelDetail && (
          <div className="space-y-4">
            {/* Filter Tabs */}
            <div className="flex gap-2 flex-wrap">
              <Button 
                variant={detailFilterType === 'all' ? 'default' : 'outline'} 
                size="sm"
                onClick={() => {
                  setDetailFilterType('all');
                  setSelectedPersonnelRecords(getFilteredPersonnelDetailRecords(selectedPersonnelDetail.personnel_id, 'all'));
                }}
              >
                Tümü ({selectedPersonnelDetail.total_days})
              </Button>
              <Button 
                variant={detailFilterType === 'cumartesi' ? 'default' : 'outline'} 
                size="sm"
                className={detailFilterType === 'cumartesi' ? '' : 'bg-blue-50'}
                onClick={() => {
                  setDetailFilterType('cumartesi');
                  setSelectedPersonnelRecords(getFilteredPersonnelDetailRecords(selectedPersonnelDetail.personnel_id, 'cumartesi'));
                }}
              >
                Cumartesi ({selectedPersonnelDetail.cumartesi_count})
              </Button>
              <Button 
                variant={detailFilterType === 'pazar' ? 'default' : 'outline'} 
                size="sm"
                className={detailFilterType === 'pazar' ? '' : 'bg-blue-50'}
                onClick={() => {
                  setDetailFilterType('pazar');
                  setSelectedPersonnelRecords(getFilteredPersonnelDetailRecords(selectedPersonnelDetail.personnel_id, 'pazar'));
                }}
              >
                Pazar ({selectedPersonnelDetail.pazar_count})
              </Button>
              <Button 
                variant={detailFilterType === 'resmi_tatil' ? 'default' : 'outline'} 
                size="sm"
                className={detailFilterType === 'resmi_tatil' ? '' : 'bg-red-50'}
                onClick={() => {
                  setDetailFilterType('resmi_tatil');
                  setSelectedPersonnelRecords(getFilteredPersonnelDetailRecords(selectedPersonnelDetail.personnel_id, 'resmi_tatil'));
                }}
              >
                Resmi Tatil ({selectedPersonnelDetail.resmi_tatil_count})
              </Button>
              <Button 
                variant={detailFilterType === 'idari_tatil' ? 'default' : 'outline'} 
                size="sm"
                className={detailFilterType === 'idari_tatil' ? '' : 'bg-purple-50'}
                onClick={() => {
                  setDetailFilterType('idari_tatil');
                  setSelectedPersonnelRecords(getFilteredPersonnelDetailRecords(selectedPersonnelDetail.personnel_id, 'idari_tatil'));
                }}
              >
                İdari Tatil ({selectedPersonnelDetail.idari_tatil_count || 0})
              </Button>
              <Button 
                variant={detailFilterType === 'aksam_mesaisi' ? 'default' : 'outline'} 
                size="sm"
                className={detailFilterType === 'aksam_mesaisi' ? '' : 'bg-orange-50'}
                onClick={() => {
                  setDetailFilterType('aksam_mesaisi');
                  setSelectedPersonnelRecords(getFilteredPersonnelDetailRecords(selectedPersonnelDetail.personnel_id, 'aksam_mesaisi'));
                }}
              >
                Akşam ({selectedPersonnelDetail.aksam_mesaisi_count || 0})
              </Button>
              <Button 
                variant={detailFilterType === 'gece_mesaisi' ? 'default' : 'outline'} 
                size="sm"
                className={detailFilterType === 'gece_mesaisi' ? '' : 'bg-indigo-50'}
                onClick={() => {
                  setDetailFilterType('gece_mesaisi');
                  setSelectedPersonnelRecords(getFilteredPersonnelDetailRecords(selectedPersonnelDetail.personnel_id, 'gece_mesaisi'));
                }}
              >
                Gece ({selectedPersonnelDetail.gece_mesaisi_count || 0})
              </Button>
            </div>

            {/* Records Table - Aynı tarih için birden fazla park kaydı olabilir */}
            <div className="border rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-100 border-b">
                    <th className="text-left py-2 px-3">Tarih</th>
                    <th className="text-left py-2 px-3">Gün</th>
                    <th className="text-left py-2 px-3">Mesai Türü</th>
                    <th className="text-left py-2 px-3">Konum</th>
                    <th className="text-center py-2 px-3">Saat Aralığı</th>
                    <th className="text-center py-2 px-3">Süre</th>
                    <th className="text-center py-2 px-3">Durum</th>
                  </tr>
                </thead>
                <tbody>
                  {(() => {
                    // Tarihlere göre grupla - akşam/gece birleşimi için özel mantık
                    const dateGroups = {};
                    selectedPersonnelRecords.forEach(r => {
                      if (!dateGroups[r.date]) {
                        dateGroups[r.date] = {
                          date: r.date,
                          day_types: new Set(),
                          entries: [],
                          aksamEntry: null,
                          geceEntry: null,
                          totalHours: 0,
                          totalCancelledHours: 0
                        };
                      }
                      
                      const effectiveType = r.effective_day_type || r.day_type;
                      dateGroups[r.date].day_types.add(effectiveType);
                      
                      const entry = {
                        park_name: r.personnel_role === 'bolge_muhendisi'
                          ? '📋 Bölge Denetimi'
                          : r.personnel_role === 'formen'
                            ? '📋 Alan Denetimi'
                            : (r.park_name || '—'),
                        start_time: r.start_time || '08:00',
                        end_time: r.end_time || '17:00',
                        hours_worked: r.hours_worked || 8,
                        shift_type: r.shift_type || 'full_day',
                        day_type: effectiveType,
                        cancelled: r.cancelled || false,
                        cancel_reason: r.cancel_reason,
                        cancel_note: r.cancel_note,
                        cancelled_by_name: r.cancelled_by_name,
                        cancelled_at: r.cancelled_at
                      };
                      
                      // Akşam/gece kayıtlarını ayrı takip et
                      if (effectiveType === 'aksam_mesaisi') {
                        dateGroups[r.date].aksamEntry = entry;
                      } else if (effectiveType === 'gece_mesaisi') {
                        dateGroups[r.date].geceEntry = entry;
                      }
                      
                      dateGroups[r.date].entries.push(entry);
                      
                      if (r.cancelled) {
                        dateGroups[r.date].totalCancelledHours += (r.hours_worked || 8);
                      } else {
                        dateGroups[r.date].totalHours += (r.hours_worked || 8);
                      }
                    });
                    
                    // Tarihe göre sırala (en yeni en üstte)
                    const sortedDates = Object.keys(dateGroups).sort((a, b) => b.localeCompare(a));
                    
                    return sortedDates.map((date, index) => {
                      const group = dateGroups[date];
                      const dayTypes = Array.from(group.day_types);
                      const hasAksam = dayTypes.includes('aksam_mesaisi');
                      const hasGece = dayTypes.includes('gece_mesaisi');
                      const isAksamGece = hasAksam || hasGece;
                      
                      // Akşam/gece dışı kayıtlar için max 8 saat
                      const displayHours = isAksamGece ? group.totalHours : Math.min(group.totalHours, 8);
                      const hasMultipleEntries = group.entries.length > 1;
                      const hasCancelled = group.entries.some(e => e.cancelled);
                      const allCancelled = group.entries.every(e => e.cancelled);
                      
                      // Akşam + Gece birleşik gösterim
                      const aksamHours = group.aksamEntry?.hours_worked || 0;
                      const geceHours = group.geceEntry?.hours_worked || 0;
                      
                      return (
                        <tr key={date} className={`${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'} ${isAksamGece ? 'border-l-4 border-l-orange-400' : hasMultipleEntries ? 'border-l-4 border-l-blue-400' : ''} ${allCancelled ? 'bg-red-50' : ''}`}>
                          <td className={`py-2 px-3 font-medium ${allCancelled ? 'line-through text-gray-400' : ''}`}>
                            {format(parseISO(date), 'd MMMM yyyy', { locale: tr })}
                            {/* Akşam/gece için hangi mesai iptal edilmiş göster */}
                            {isAksamGece && (group.aksamEntry?.cancelled || group.geceEntry?.cancelled) && !allCancelled && (
                              <div className="text-[10px] text-red-500 mt-0.5">
                                {group.aksamEntry?.cancelled && group.geceEntry?.cancelled ? '(akşam+gece iptal)' :
                                 group.aksamEntry?.cancelled ? '(akşam iptal)' : '(gece iptal)'}
                              </div>
                            )}
                          </td>
                          <td className={`py-2 px-3 ${allCancelled ? 'line-through text-gray-400' : ''}`}>
                            {format(parseISO(date), 'EEEE', { locale: tr })}
                          </td>
                          <td className="py-2 px-3">
                            {isAksamGece ? (
                              <Badge className="bg-gradient-to-r from-orange-100 to-indigo-100 text-orange-800 border border-orange-300">
                                Akşam/Gece
                              </Badge>
                            ) : (
                              <Badge className={`${dayTypeColors[dayTypes[0]] || 'bg-gray-200 text-gray-700'}`}>
                                {dayTypeLabels[dayTypes[0]] || dayTypes[0]}
                              </Badge>
                            )}
                          </td>
                          <td className="py-2 px-3">
                            <div className="flex flex-col gap-1">
                              {/* Akşam/Gece için tek satırda göster */}
                              {isAksamGece ? (
                                <span className="font-medium text-sm">{group.entries[0]?.park_name}</span>
                              ) : (
                                group.entries.map((entry, i) => (
                                  <div key={i} className={`text-xs ${hasMultipleEntries ? 'bg-blue-50 px-2 py-1 rounded' : ''} ${entry.cancelled ? 'line-through text-gray-400' : ''}`}>
                                    <span className="font-medium">{entry.park_name}</span>
                                  </div>
                                ))
                              )}
                            </div>
                          </td>
                          <td className="text-center py-2 px-3">
                            {isAksamGece ? (
                              <div className="flex flex-col gap-0.5">
                                {hasAksam && group.aksamEntry && (
                                  <div className={`text-xs ${group.aksamEntry.cancelled ? 'line-through text-gray-400' : 'text-orange-600'}`}>
                                    {group.aksamEntry.start_time} - {group.aksamEntry.end_time}
                                  </div>
                                )}
                                {hasGece && group.geceEntry && (
                                  <div className={`text-xs ${group.geceEntry.cancelled ? 'line-through text-gray-400' : 'text-indigo-600'}`}>
                                    {group.geceEntry.start_time} - {group.geceEntry.end_time}
                                  </div>
                                )}
                              </div>
                            ) : (
                              <div className="flex flex-col gap-1">
                                {group.entries.map((entry, i) => (
                                  <div key={i} className={`text-xs ${entry.shift_type === 'hourly' ? 'text-orange-600 font-medium' : 'text-gray-600'} ${entry.cancelled ? 'line-through text-gray-400' : ''}`}>
                                    {entry.start_time} - {entry.end_time}
                                  </div>
                                ))}
                              </div>
                            )}
                          </td>
                          <td className="text-center py-2 px-3">
                            {isAksamGece ? (
                              <div className="flex flex-col items-center">
                                {hasAksam && aksamHours > 0 && (
                                  <span className={`text-xs ${group.aksamEntry?.cancelled ? 'line-through text-gray-400' : 'text-gray-400'}`}>{aksamHours} saat</span>
                                )}
                                {hasGece && geceHours > 0 && (
                                  <span className={`text-xs ${group.geceEntry?.cancelled ? 'line-through text-gray-400' : 'text-gray-400'}`}>{geceHours} saat</span>
                                )}
                                <span className="font-bold text-orange-700">
                                  {(group.aksamEntry?.cancelled ? 0 : aksamHours) + (group.geceEntry?.cancelled ? 0 : geceHours)} saat
                                </span>
                              </div>
                            ) : (
                              <>
                                <span className={`font-medium ${allCancelled ? 'line-through text-gray-400' : displayHours !== 8 ? 'text-orange-600' : ''}`}>
                                  {allCancelled ? '0' : displayHours} saat
                                </span>
                                {hasMultipleEntries && (
                                  <div className="text-xs text-gray-500">
                                    ({group.entries.length} park)
                                  </div>
                                )}
                              </>
                            )}
                          </td>
                          <td className="text-center py-2 px-3">
                            {hasCancelled ? (
                              <div className="space-y-1">
                                {group.entries.filter(e => e.cancelled).map((entry, i) => (
                                  <div key={i} className="text-xs">
                                    <Badge className="bg-red-100 text-red-700 border-red-300">
                                      İPTAL
                                    </Badge>
                                    <div className="text-red-600 mt-1">
                                      <span className="font-medium">
                                        {cancelReasons.find(r => r.value === entry.cancel_reason)?.label || entry.cancel_reason}
                                      </span>
                                      {entry.cancel_note && (
                                        <p className="text-gray-500 italic">"{entry.cancel_note}"</p>
                                      )}
                                      {entry.cancelled_by_name && (
                                        <p className="text-gray-400 text-[10px]">
                                          İptal eden: {entry.cancelled_by_name}
                                        </p>
                                      )}
                                    </div>
                                  </div>
                                ))}
                                {group.entries.filter(e => !e.cancelled).length > 0 && (
                                  <Badge className="bg-green-100 text-green-700">
                                    Aktif
                                  </Badge>
                                )}
                              </div>
                            ) : (
                              <Badge className="bg-green-100 text-green-700">
                                Aktif
                              </Badge>
                            )}
                          </td>
                        </tr>
                      );
                    });
                  })()}
                  {selectedPersonnelRecords.length === 0 && (
                    <tr>
                      <td colSpan={7} className="text-center py-8 text-gray-500">
                        Kayıt bulunamadı
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            
            {/* Summary - Aynı tarih için birden fazla park kaydı olabilir, gün sayısını unique tarihlerden hesapla */}
            <div className="bg-gray-50 p-3 rounded-lg">
              {(() => {
                // Tarihlere göre grupla
                const dateGroups = {};
                selectedPersonnelRecords.forEach(r => {
                  if (!dateGroups[r.date]) {
                    dateGroups[r.date] = { records: [], totalHours: 0 };
                  }
                  dateGroups[r.date].records.push(r);
                });
                
                // Her gün için maksimum 8 saat hesapla
                let totalDays = Object.keys(dateGroups).length;
                let totalHours = 0;
                Object.values(dateGroups).forEach(group => {
                  const dayHours = Math.min(
                    group.records.reduce((sum, r) => sum + (r.hours_worked || 8), 0),
                    8
                  );
                  totalHours += dayHours;
                });
                
                return (
                  <p className="text-sm text-gray-600">
                    <strong>Toplam:</strong> {totalDays} gün, {totalHours} saat mesai
                    {selectedPersonnelRecords.length > totalDays && (
                      <span className="ml-2 text-xs text-blue-600">
                        ({selectedPersonnelRecords.length} park kaydı - aynı gün birden fazla parka çalışıldı)
                      </span>
                    )}
                  </p>
                );
              })()}
            </div>
          </div>
        )}
      </DraggableModal>

      {/* Schedule Detail Dialog - Tek Mesai Planı Detayı */}
      <DraggableModal
        isOpen={showScheduleDetailDialog}
        onClose={() => setShowScheduleDetailDialog(false)}
        title={selectedScheduleDetail ? `${selectedScheduleDetail.park_name} - Mesai Detayı` : 'Mesai Detayı'}
        maxWidth={selectedScheduleDetail?.is_aksam_gece_combined ? "max-w-4xl" : "max-w-lg"}
      >
        {selectedScheduleDetail && selectedScheduleDate && (
          <div className="space-y-4">
            {/* Geri Butonu - Gün dialoguna dön */}
            <Button 
              variant="outline" 
              size="sm" 
              className="bg-blue-50 hover:bg-blue-100 text-blue-700 border-blue-300 font-medium"
              onClick={() => {
                setShowScheduleDetailDialog(false);
                // Kısa gecikmeyle gün dialogunu aç
                setTimeout(() => {
                  setShowDayDialog(true);
                }, 100);
              }}
            >
              <ChevronLeft className="h-5 w-5 mr-1" />
              ← Geri Dön
            </Button>

            {/* Tarih Bilgisi */}
            <div className="p-3 bg-gray-50 rounded-lg border">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Tarih</p>
                  <p className="font-semibold">{format(selectedScheduleDate, 'd MMMM yyyy, EEEE', { locale: tr })}</p>
                  {selectedScheduleDetail.created_by_name && (
                    <p className="text-xs text-gray-500 mt-1">
                      Oluşturan: <span className="font-medium text-gray-700">{selectedScheduleDetail.created_by_name}</span>
                    </p>
                  )}
                </div>
                <Badge className={
                  getDayType(selectedScheduleDate)?.type === 'resmi_tatil' ? 'bg-red-200 text-red-800' :
                  getDayType(selectedScheduleDate)?.type === 'idari_tatil' ? 'bg-purple-200 text-purple-800' :
                  getDayType(selectedScheduleDate)?.type === 'cumartesi' || getDayType(selectedScheduleDate)?.type === 'pazar' ? 'bg-blue-200 text-blue-800' :
                  'bg-gray-200'
                }>
                  {getDayType(selectedScheduleDate)?.name || 'Normal Gün'}
                </Badge>
              </div>
            </div>

            {/* Park Bilgisi */}
            <div className="p-3 bg-green-50 rounded-lg border border-green-200">
              <p className="text-sm text-gray-500">Park</p>
              <p className="font-semibold text-green-700 text-lg">{selectedScheduleDetail.park_name}</p>
            </div>

            {/* Açıklama / Yapılan İş (mesaide ne yapıldı) */}
            {(selectedScheduleDetail.note || selectedScheduleDetail.aksam_schedule?.note || selectedScheduleDetail.gece_schedule?.note) && (
              <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
                <p className="text-sm text-gray-500">📝 Açıklama (Yapılan İş)</p>
                <p className="font-medium text-amber-800 break-words">
                  {selectedScheduleDetail.note || selectedScheduleDetail.aksam_schedule?.note || selectedScheduleDetail.gece_schedule?.note}
                </p>
              </div>
            )}

            {/* Formen / Bölge Müh. katılım durumu + katılmama sebebi */}
            {(selectedScheduleDetail.formen_name || selectedScheduleDetail.bolge_muh_name) && (
              <div className="p-3 bg-blue-50 rounded-lg border border-blue-200 space-y-1.5">
                {selectedScheduleDetail.formen_name && (
                  <div className="text-sm">
                    <span className="text-gray-500">👤 Formen: </span>
                    <span className="font-medium">{selectedScheduleDetail.formen_name}</span>
                    {selectedScheduleDetail.formen_joining === false ? (
                      <span className="text-orange-600"> — Katılmadı{selectedScheduleDetail.formen_not_joining_reason ? ` (${selectedScheduleDetail.formen_not_joining_reason})` : ''}</span>
                    ) : (
                      <span className="text-green-600"> — Mesaiye katıldı</span>
                    )}
                  </div>
                )}
                {selectedScheduleDetail.bolge_muh_name && (
                  <div className="text-sm">
                    <span className="text-gray-500">🔵 Bölge Müh.: </span>
                    <span className="font-medium">{selectedScheduleDetail.bolge_muh_name}</span>
                    {selectedScheduleDetail.bolge_muh_joining === false ? (
                      <span className="text-orange-600"> — Katılmadı{selectedScheduleDetail.bolge_muh_not_joining_reason ? ` (${selectedScheduleDetail.bolge_muh_not_joining_reason})` : ''}</span>
                    ) : (
                      <span className="text-green-600"> — Mesaiye katıldı</span>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Atanan Personel */}
            <div className="space-y-2">
              <h4 className="font-semibold flex items-center gap-2">
                <Users className="h-4 w-4" />
                Atanan Personel ({selectedScheduleDetail.assigned_personnel?.length || 0})
              </h4>
              
              {/* Akşam/Gece Birleşik Tablo */}
              {selectedScheduleDetail.is_aksam_gece_combined ? (
                <div className="border rounded-lg overflow-hidden">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-gray-100 border-b">
                        <th className="text-left py-2 px-3" rowSpan={2}>Personel</th>
                        <th className="text-center py-2 px-2 bg-orange-50 border-l" colSpan={canMutateMesai ? 3 : 2}>
                          <span className="text-orange-600 font-medium">🌙 Akşam</span>
                        </th>
                        <th className="text-center py-2 px-2 bg-indigo-50 border-l" colSpan={canMutateMesai ? 3 : 2}>
                          <span className="text-indigo-600 font-medium">🌙 Gece</span>
                        </th>
                      </tr>
                      <tr className="bg-gray-50 border-b text-xs">
                        <th className="text-center py-1 px-2 bg-orange-50/50 border-l">Saat Aralığı</th>
                        <th className="text-center py-1 px-2 bg-orange-50/50">Süre</th>
                        {canMutateMesai && <th className="text-center py-1 px-2 bg-orange-50/50">İşlem</th>}
                        <th className="text-center py-1 px-2 bg-indigo-50/50 border-l">Saat Aralığı</th>
                        <th className="text-center py-1 px-2 bg-indigo-50/50">Süre</th>
                        {canMutateMesai && <th className="text-center py-1 px-2 bg-indigo-50/50">İşlem</th>}
                      </tr>
                    </thead>
                    <tbody>
                      {(() => {
                        // Personel bazında akşam ve gece kayıtlarını eşleştir
                        const aksamPersonnel = selectedScheduleDetail.aksam_schedule?.assigned_personnel || [];
                        const gecePersonnel = selectedScheduleDetail.gece_schedule?.assigned_personnel || [];
                        
                        // Tüm benzersiz personeli bul
                        const allPersonnelIds = new Set([
                          ...aksamPersonnel.map(p => p.personnel_id),
                          ...gecePersonnel.map(p => p.personnel_id)
                        ]);
                        
                        return Array.from(allPersonnelIds).map((pid, index) => {
                          const aksamP = aksamPersonnel.find(p => p.personnel_id === pid);
                          const geceP = gecePersonnel.find(p => p.personnel_id === pid);
                          const personData = aksamP || geceP;
                          
                          return (
                            <React.Fragment key={pid}>
                            <tr className={`${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}>
                              {/* Personel Adı */}
                              <td className="py-2 px-3">
                                <div>
                                  <span className="font-medium">{personData.personnel_name}</span>
                                  {personData.personnel_role && (
                                    <span className="text-xs text-gray-500 ml-1">({roleLabels[personData.personnel_role] || personData.personnel_role})</span>
                                  )}
                                </div>
                              </td>
                              
                              {/* Akşam Mesaisi */}
                              <td className={`text-center py-2 px-2 border-l ${aksamP?.cancelled ? 'bg-red-50/50' : 'bg-orange-50/30'}`}>
                                {aksamP ? (
                                  <span className={`text-orange-600 ${aksamP.cancelled ? 'line-through text-gray-400' : ''}`}>
                                    {aksamP.start_time} - {aksamP.end_time}
                                  </span>
                                ) : <span className="text-gray-300">-</span>}
                              </td>
                              <td className={`text-center py-2 px-2 ${aksamP?.cancelled ? 'bg-red-50/50' : 'bg-orange-50/30'}`}>
                                {aksamP ? (
                                  <Badge variant="outline" className={`text-xs ${aksamP.cancelled ? 'bg-gray-100 text-gray-400 line-through' : 'bg-orange-100 text-orange-700'}`}>
                                    {aksamP.hours_worked} saat
                                  </Badge>
                                ) : <span className="text-gray-300">-</span>}
                              </td>
                              {canMutateMesai && (
                                <td className={`text-center py-2 px-2 ${aksamP?.cancelled ? 'bg-red-50/50' : 'bg-orange-50/30'}`}>
                                  {aksamP && (
                                    !aksamP.cancelled ? (
                                      <Button 
                                        variant="ghost" 
                                        size="sm"
                                        className="h-6 px-2 text-xs text-red-600 hover:bg-red-50"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setCancelTargetRecord({
                                            id: aksamP.record_id || aksamP.id,
                                            personnel_id: aksamP.personnel_id,
                                            personnel_name: aksamP.personnel_name,
                                            park_name: selectedScheduleDetail.park_name,
                                            date: format(selectedScheduleDate, 'd MMMM yyyy', { locale: tr }),
                                            date_raw: format(selectedScheduleDate, 'yyyy-MM-dd'),
                                            day_type: 'aksam_mesaisi'
                                          });
                                          setShowCancelDialog(true);
                                        }}
                                      >
                                        <XCircle className="h-3 w-3 mr-1" />
                                        İptal
                                      </Button>
                                    ) : (
                                      <Button 
                                        variant="ghost" 
                                        size="sm"
                                        className="h-6 px-2 text-xs text-green-600 hover:bg-green-50"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleRestoreOvertimeRecord(aksamP.record_id || aksamP.id);
                                        }}
                                      >
                                        <RefreshCw className="h-3 w-3 mr-1" />
                                        Geri Al
                                      </Button>
                                    )
                                  )}
                                </td>
                              )}
                              
                              {/* Gece Mesaisi */}
                              <td className={`text-center py-2 px-2 border-l ${geceP?.cancelled ? 'bg-red-50/50' : 'bg-indigo-50/30'}`}>
                                {geceP ? (
                                  <span className={`text-indigo-600 ${geceP.cancelled ? 'line-through text-gray-400' : ''}`}>
                                    {geceP.start_time} - {geceP.end_time}
                                  </span>
                                ) : <span className="text-gray-300">-</span>}
                              </td>
                              <td className={`text-center py-2 px-2 ${geceP?.cancelled ? 'bg-red-50/50' : 'bg-indigo-50/30'}`}>
                                {geceP ? (
                                  <Badge variant="outline" className={`text-xs ${geceP.cancelled ? 'bg-gray-100 text-gray-400 line-through' : 'bg-indigo-100 text-indigo-700'}`}>
                                    {geceP.hours_worked} saat
                                  </Badge>
                                ) : <span className="text-gray-300">-</span>}
                              </td>
                              {canMutateMesai && (
                                <td className={`text-center py-2 px-2 ${geceP?.cancelled ? 'bg-red-50/50' : 'bg-indigo-50/30'}`}>
                                  {geceP && (
                                    !geceP.cancelled ? (
                                      <Button 
                                        variant="ghost" 
                                        size="sm"
                                        className="h-6 px-2 text-xs text-red-600 hover:bg-red-50"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setCancelTargetRecord({
                                            id: geceP.record_id || geceP.id,
                                            personnel_id: geceP.personnel_id,
                                            personnel_name: geceP.personnel_name,
                                            park_name: selectedScheduleDetail.park_name,
                                            date: format(selectedScheduleDate, 'd MMMM yyyy', { locale: tr }),
                                            date_raw: format(selectedScheduleDate, 'yyyy-MM-dd'),
                                            day_type: 'gece_mesaisi'
                                          });
                                          setShowCancelDialog(true);
                                        }}
                                      >
                                        <XCircle className="h-3 w-3 mr-1" />
                                        İptal
                                      </Button>
                                    ) : (
                                      <Button 
                                        variant="ghost" 
                                        size="sm"
                                        className="h-6 px-2 text-xs text-green-600 hover:bg-green-50"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleRestoreOvertimeRecord(geceP.record_id || geceP.id);
                                        }}
                                      >
                                        <RefreshCw className="h-3 w-3 mr-1" />
                                        Geri Al
                                      </Button>
                                    )
                                  )}
                                </td>
                              )}
                            </tr>
                            {/* İptal bilgisi satırı - akşam veya gece iptal edildiyse göster */}
                            {(aksamP?.cancelled || geceP?.cancelled) && (
                              <tr className="bg-red-50/30">
                                <td colSpan={canMutateMesai ? 7 : 5} className="py-1 px-3 text-xs">
                                  <div className="flex flex-wrap gap-4">
                                    {aksamP?.cancelled && (
                                      <div className="text-red-600">
                                        <strong>Akşam İptal:</strong> {cancelReasons.find(r => r.value === aksamP.cancel_reason)?.label || aksamP.cancel_reason}
                                        {aksamP.cancel_note && <span className="text-gray-500"> - {aksamP.cancel_note}</span>}
                                        {aksamP.cancelled_by_name && <span className="text-gray-400 ml-1">(İptal eden: {aksamP.cancelled_by_name})</span>}
                                      </div>
                                    )}
                                    {geceP?.cancelled && (
                                      <div className="text-red-600">
                                        <strong>Gece İptal:</strong> {cancelReasons.find(r => r.value === geceP.cancel_reason)?.label || geceP.cancel_reason}
                                        {geceP.cancel_note && <span className="text-gray-500"> - {geceP.cancel_note}</span>}
                                        {geceP.cancelled_by_name && <span className="text-gray-400 ml-1">(İptal eden: {geceP.cancelled_by_name})</span>}
                                      </div>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            )}
                            </React.Fragment>
                          );
                        });
                      })()}
                    </tbody>
                  </table>
                  
                  {/* Toplam Özeti */}
                  <div className="p-3 bg-gradient-to-r from-orange-50 to-indigo-50 border-t">
                    <div className="flex justify-between items-center text-sm">
                      <div>
                        <strong>Toplam:</strong> {selectedScheduleDetail.assigned_personnel?.length || 0} personel
                      </div>
                      <div className="flex gap-4">
                        <span className="text-orange-600">
                          <strong>Akşam:</strong> {selectedScheduleDetail.aksam_hours || 0} saat
                        </span>
                        <span className="text-indigo-600">
                          <strong>Gece:</strong> {selectedScheduleDetail.gece_hours || 0} saat
                        </span>
                        <span className="font-bold">
                          Toplam: {selectedScheduleDetail.total_hours || 0} saat
                        </span>
                      </div>
                    </div>
                    {selectedScheduleDetail.created_by_name && (
                      <p className="text-xs text-gray-500 mt-1">
                        <strong>Oluşturan:</strong> {selectedScheduleDetail.created_by_name}
                      </p>
                    )}
                  </div>
                </div>
              ) : (
                /* Normal (tek tip) Mesai Tablosu */
                <div className="border rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-100 border-b">
                      <th className="text-left py-2 px-3">Personel</th>
                      <th className="text-center py-2 px-3">Saat Aralığı</th>
                      <th className="text-center py-2 px-3">Süre</th>
                      {canMutateMesai && <th className="text-center py-2 px-3">İşlem</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {selectedScheduleDetail.assigned_personnel?.map((p, index) => (
                      <tr key={p.personnel_id} className={`${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'} ${p.cancelled ? 'opacity-50 bg-red-50' : ''}`}>
                        <td className="py-2 px-3">
                          <div className={p.cancelled ? 'line-through' : ''}>
                            <span className="font-medium">{p.personnel_name}</span>
                            {p.personnel_role && (
                              <span className="text-xs text-gray-500 ml-1">({p.personnel_role})</span>
                            )}
                            {p.cancelled && (
                              <Badge variant="outline" className="ml-2 text-xs bg-red-100 text-red-700 border-red-300">
                                İPTAL
                              </Badge>
                            )}
                          </div>
                          {p.cancelled && p.cancel_reason && (
                            <div className="text-xs mt-1 space-y-0.5">
                              <p className="text-red-600">
                                <span className="font-medium">Sebep:</span> {cancelReasons.find(r => r.value === p.cancel_reason)?.label || p.cancel_reason}
                              </p>
                              {p.cancel_note && (
                                <p className="text-gray-500 italic">"{p.cancel_note}"</p>
                              )}
                              {p.cancelled_by_name && (
                                <p className="text-gray-400">
                                  <span className="font-medium">İptal eden:</span> {p.cancelled_by_name}
                                </p>
                              )}
                            </div>
                          )}
                        </td>
                        <td className="text-center py-2 px-3">
                          <span className={`${p.shift_type === 'hourly' ? 'text-orange-600 font-medium' : 'text-gray-600'} ${p.cancelled ? 'line-through' : ''}`}>
                            {p.start_time || '08:00'} - {p.end_time || '17:00'}
                          </span>
                        </td>
                        <td className="text-center py-2 px-3">
                          <Badge variant={p.shift_type === 'hourly' ? 'outline' : 'secondary'} 
                                 className={`${p.shift_type === 'hourly' ? 'bg-orange-50 text-orange-700' : ''} ${p.cancelled ? 'line-through' : ''}`}>
                            {p.hours_worked || 8} saat
                          </Badge>
                        </td>
                        {canMutateMesai && (
                          <td className="text-center py-2 px-3">
                            {!p.cancelled ? (
                              <Button 
                                variant="ghost" 
                                size="sm"
                                className="h-7 px-2 text-red-600 hover:bg-red-50"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setCancelTargetRecord({
                                    id: p.record_id || p.id,
                                    personnel_id: p.personnel_id,
                                    personnel_name: p.personnel_name,
                                    park_name: selectedScheduleDetail.park_name,
                                    date: format(selectedScheduleDate, 'd MMMM yyyy', { locale: tr }),
                                    date_raw: format(selectedScheduleDate, 'yyyy-MM-dd') // Backend için YYYY-MM-DD format
                                  });
                                  setShowCancelDialog(true);
                                }}
                              >
                                <XCircle className="h-4 w-4 mr-1" />
                                İptal
                              </Button>
                            ) : (
                              <Button 
                                variant="ghost" 
                                size="sm"
                                className="h-7 px-2 text-green-600 hover:bg-green-50"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleRestoreOvertimeRecord(p.record_id || p.id);
                                }}
                              >
                                <RefreshCw className="h-4 w-4 mr-1" />
                                Geri Al
                              </Button>
                            )}
                          </td>
                        )}
                      </tr>
                    ))}
                    {(!selectedScheduleDetail.assigned_personnel || selectedScheduleDetail.assigned_personnel.length === 0) && (
                      <tr>
                        <td colSpan={canMutateMesai ? 4 : 3} className="text-center py-4 text-gray-500">
                          Henüz personel atanmamış
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              )}

            {/* Mobil Ekip Formen Bilgisi */}
            {selectedScheduleDetail.schedule_type === 'mobile' && selectedScheduleDetail.formen_name && (
              <div className={`p-3 rounded-lg border ${selectedScheduleDetail.formen_joining === false ? 'bg-orange-50 border-orange-200' : 'bg-purple-50 border-purple-200'}`}>
                <p className={`text-sm font-medium ${selectedScheduleDetail.formen_joining === false ? 'text-orange-700' : 'text-purple-700'}`}>
                  👤 Formen: {selectedScheduleDetail.formen_name}
                  {selectedScheduleDetail.formen_joining === false ? (
                    <span className="text-orange-600"> - Katılmıyor</span>
                  ) : (
                    <span className="text-green-600"> - Katılıyor ✓</span>
                  )}
                </p>
                {selectedScheduleDetail.formen_joining === false && selectedScheduleDetail.formen_not_joining_reason && (
                  <p className="text-xs text-orange-600 mt-1 bg-orange-100 p-2 rounded">
                    <strong>Sebep:</strong> {selectedScheduleDetail.formen_not_joining_reason}
                  </p>
                )}
              </div>
            )}

            {/* Özet - sadece normal (birleşik olmayan) mesailer için */}
            {!selectedScheduleDetail.is_aksam_gece_combined && (
            <div className="bg-blue-50 p-3 rounded-lg border border-blue-200">
              <p className="text-sm text-blue-700">
                <strong>Toplam:</strong> {selectedScheduleDetail.assigned_personnel?.filter(p => !p.cancelled).length || 0} personel, {' '}
                {Math.round(selectedScheduleDetail.assigned_personnel?.filter(p => !p.cancelled).reduce((sum, p) => sum + (parseFloat(p.hours_worked) || 8), 0) || 0)} saat mesai
                {selectedScheduleDetail.assigned_personnel?.some(p => p.cancelled) && (
                  <span className="text-red-600 ml-2">
                    ({selectedScheduleDetail.assigned_personnel?.filter(p => p.cancelled).length} iptal)
                  </span>
                )}
              </p>
              {selectedScheduleDetail.created_by_name && (
                <p className="text-xs text-blue-600 mt-1">
                  <strong>Oluşturan:</strong> {selectedScheduleDetail.created_by_name}
                </p>
              )}
            </div>
            )}
            </div>

            {/* Tüm planı silme butonu - amir rolleri + yalnızca kendi kapsamındaki plan */}
            {canMutateScheduleScope(selectedScheduleDetail) && (
              <div className="pt-2 border-t">
                <Button
                  variant="outline"
                  className="w-full text-red-600 hover:bg-red-50 border-red-200"
                  onClick={() => {
                    setShowScheduleDetailDialog(false);
                    openDeleteConfirm('duty', selectedScheduleDetail.id, selectedScheduleDetail.park_name);
                  }}
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Tüm Mesai Planını Sil
                </Button>
              </div>
            )}
          </div>
        )}
      </DraggableModal>

      {/* Mesai İptal Dialogu */}
      <DraggableModal
        isOpen={showCancelDialog}
        onClose={() => {
          setShowCancelDialog(false);
          setCancelTargetRecord(null);
          setCancelReason('');
          setCancelNote('');
        }}
        title="Mesai İptali"
        maxWidth="max-w-md"
      >
        {cancelTargetRecord && (
          <div className="space-y-4">
            <div className="p-3 bg-orange-50 rounded-lg border border-orange-200">
              <p className="text-sm text-orange-800">
                <strong>{cancelTargetRecord.personnel_name}</strong> personelinin{' '}
                <strong>{cancelTargetRecord.date}</strong> tarihli{' '}
                <strong>{cancelTargetRecord.park_name}</strong> mesaisini iptal etmek üzeresiniz.
              </p>
            </div>

            <div className="space-y-2">
              <Label className="font-medium">İptal Sebebi <span className="text-red-500">*</span></Label>
              <SearchableSelect
                value={cancelReason}
                onChange={setCancelReason}
                showAllOption={false}
                placeholder="Sebep seçin..."
                searchPlaceholder="Sebep ara..."
                options={cancelReasons.map(reason => ({ id: reason.value, name: reason.label }))}
              />
            </div>

            <div className="space-y-2">
              <Label className="font-medium">Açıklama (İsteğe bağlı)</Label>
              <Textarea
                placeholder="Ek açıklama yazın..."
                value={cancelNote}
                onChange={(e) => setCancelNote(e.target.value)}
                rows={2}
              />
            </div>

            <div className="flex gap-2 pt-2">
              <Button 
                variant="outline" 
                className="flex-1"
                onClick={() => {
                  setShowCancelDialog(false);
                  setCancelTargetRecord(null);
                  setCancelReason('');
                  setCancelNote('');
                }}
              >
                Vazgeç
              </Button>
              <Button 
                className="flex-1 bg-red-600 hover:bg-red-700"
                onClick={handleCancelOvertimeRecord}
                disabled={!cancelReason || loading}
              >
                {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <XCircle className="h-4 w-4 mr-2" />}
                İptal Et
              </Button>
            </div>
          </div>
        )}
      </DraggableModal>

      {/* Mobil Ekip Oluşturma Dialogu */}
      <DraggableModal 
        isOpen={showMobileTeamDialog} 
        onClose={() => {
          setShowMobileTeamDialog(false);
          setMobileTeamPersonnel([]);
          setMobileTeamNote('');
          setMobilePersonnelSearch('');
          setSelectedMobileFormen(null);
          setSelectedMobileBolgeMuh(null);
          setFormenJoining(true);
          setFormenNotJoiningReason('');
          setBolgeMuhJoining(false);
          setBolgeMuhNotJoiningReason('');
        }}
        title={`🚐 Mobil Ekip Oluştur${mobileTeamDate ? ` - ${format(mobileTeamDate, 'd MMMM yyyy', { locale: tr })}` : ''}`}
        maxWidth="max-w-3xl"
      >
        <div className="space-y-4">
          {/* Bilgi Kartı */}
          <div className="p-3 bg-purple-50 border border-purple-200 rounded-lg">
            <p className="text-sm text-purple-700">
              <strong>Mobil Ekip:</strong> Belirli bir parka bağlı olmadan, birden fazla parkta çalışacak gezici ekip oluşturun.
            </p>
          </div>

          {/* Saat Seçimi */}
          <div className="p-3 bg-gray-50 rounded-lg border">
            <h4 className="font-medium mb-3 flex items-center gap-2">
              <Clock className="h-4 w-4" />
              Mesai Saatleri
            </h4>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Başlangıç Saati</Label>
                <Input 
                  type="time" 
                  value={mobileTeamStartTime} 
                  onChange={(e) => setMobileTeamStartTime(e.target.value)} 
                />
              </div>
              <div className="space-y-2">
                <Label>Bitiş Saati</Label>
                <Input 
                  type="time" 
                  value={mobileTeamEndTime} 
                  onChange={(e) => setMobileTeamEndTime(e.target.value)} 
                />
              </div>
            </div>
            <div className="mt-2 text-sm text-gray-600">
              <Clock className="h-4 w-4 inline mr-1" />
              Toplam: <strong>{calculateHours(mobileTeamStartTime, mobileTeamEndTime)} saat</strong>
            </div>
          </div>

          {/* Bölge Mühendisi kendisi için katılım seçeneği - PERSONEL SEÇ'TEN ÖNCE */}
          {isBolgeMuhendisi && (
            <div className="p-3 bg-cyan-50 rounded-lg border border-cyan-200 space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-700">
                  🔵 Ben de (Bölge Müh.) mesaiye katılacağım
                </span>
                <div className="flex items-center gap-3">
                  <label className="flex items-center gap-1 cursor-pointer">
                    <input 
                      type="radio" 
                      name="bolgeMuhSelfJoining" 
                      checked={bolgeMuhJoining} 
                      onChange={() => {
                        setBolgeMuhJoining(true);
                        setBolgeMuhNotJoiningReason('');
                      }}
                      className="w-4 h-4 text-green-600"
                    />
                    <span className="text-sm text-green-700">✓ Evet</span>
                  </label>
                  <label className="flex items-center gap-1 cursor-pointer">
                    <input 
                      type="radio" 
                      name="bolgeMuhSelfJoining" 
                      checked={!bolgeMuhJoining} 
                      onChange={() => setBolgeMuhJoining(false)}
                      className="w-4 h-4 text-gray-600"
                    />
                    <span className="text-sm text-gray-700">✗ Hayır</span>
                  </label>
                </div>
              </div>
              {/* Katılmıyorsa sebep */}
              {!bolgeMuhJoining && (
                <div className="mt-2 p-2 bg-orange-50 rounded border border-orange-200">
                  <Label className="text-xs text-orange-700 mb-1 block">Katılmama Sebebi (Opsiyonel)</Label>
                  <Input
                    placeholder="Örn: İzinli, Raporlu, Toplantı..."
                    value={bolgeMuhNotJoiningReason}
                    onChange={(e) => setBolgeMuhNotJoiningReason(e.target.value)}
                    className="text-sm"
                  />
                </div>
              )}
            </div>
          )}

          {/* Personel Seçimi */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <h4 className="font-medium flex items-center gap-2">
                <Users className="h-4 w-4" />
                Personel Seç
              </h4>
              <Badge variant="secondary">
                {mobileTeamPersonnel.length} kişi seçildi
              </Badge>
            </div>
            
            {/* Müdür / Müdür Yardımcısı için Bölge Mühendisi seçimi */}
            {['mudur_yardimcisi', 'mudur'].includes(userRole) && (
              <div className="p-3 bg-cyan-50 rounded-lg border border-cyan-200 space-y-3">
                <Label className="text-cyan-700 mb-2 block">🔵 Bölge Mühendisi Seçin (Opsiyonel)</Label>
                <SearchableSelect
                  value={selectedMobileBolgeMuh || 'none'}
                  onChange={(val) => {
                    const newVal = val === 'none' ? null : val;
                    setSelectedMobileBolgeMuh(newVal);
                    setBolgeMuhJoining(newVal ? true : false);
                    setBolgeMuhNotJoiningReason('');
                  }}
                  allValue="none"
                  allLabel="Seçilmedi"
                  searchPlaceholder="Bölge mühendisi ara..."
                  options={personnel
                    .filter(p => p.role === 'bolge_muhendisi')
                    .filter(p => {
                      if (isMudur) return true;
                      if (isMudurYardimcisi) return p.mudur_yardimcisi_id === personnelId;
                      return false;
                    })
                    .sort((a, b) => a.name.localeCompare(b.name, 'tr'))
                    .map(bm => ({ id: bm.id, name: bm.name }))}
                />
                
                {/* Bölge Müh. Katılım Durumu */}
                {selectedMobileBolgeMuh && (
                  <div className="p-3 bg-white rounded-lg border border-cyan-100 space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-gray-700">
                        {personnel.find(p => p.id === selectedMobileBolgeMuh)?.name} mesaiye katılacak mı?
                      </span>
                      <div className="flex items-center gap-3">
                        <label className="flex items-center gap-1 cursor-pointer">
                          <input 
                            type="radio" 
                            name="bolgeMuhJoiningSelect" 
                            checked={bolgeMuhJoining} 
                            onChange={() => {
                              setBolgeMuhJoining(true);
                              setBolgeMuhNotJoiningReason('');
                            }}
                            className="w-4 h-4 text-green-600"
                          />
                          <span className="text-sm text-green-700">✓ Evet</span>
                        </label>
                        <label className="flex items-center gap-1 cursor-pointer">
                          <input 
                            type="radio" 
                            name="bolgeMuhJoiningSelect" 
                            checked={!bolgeMuhJoining} 
                            onChange={() => setBolgeMuhJoining(false)}
                            className="w-4 h-4 text-red-600"
                          />
                          <span className="text-sm text-red-700">✗ Hayır</span>
                        </label>
                      </div>
                    </div>
                    {/* Katılmıyorsa sebep */}
                    {!bolgeMuhJoining && (
                      <div className="mt-2 p-2 bg-orange-50 rounded border border-orange-200">
                        <Label className="text-xs text-orange-700 mb-1 block">Katılmama Sebebi (Opsiyonel)</Label>
                        <Input
                          placeholder="Örn: İzinli, Raporlu, Toplantı..."
                          value={bolgeMuhNotJoiningReason}
                          onChange={(e) => setBolgeMuhNotJoiningReason(e.target.value)}
                          className="text-sm"
                        />
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
            
            {/* Bölge Mühendisi / Müdür Yardımcısı / Müdür için Formen seçimi */}
            {['bolge_muhendisi', 'mudur_yardimcisi', 'mudur'].includes(userRole) && (
              <div className="p-3 bg-blue-50 rounded-lg border border-blue-200 space-y-3">
                <Label className="text-blue-700 mb-2 block">🟢 Formen Seçin</Label>
                <SearchableSelect
                  value={selectedMobileFormen || ''}
                  onChange={(val) => {
                    setSelectedMobileFormen(val);
                    setMobileTeamPersonnel([]); // Formen değişince seçimleri sıfırla
                    setFormenJoining(true); // Varsayılan olarak katılıyor
                    setFormenNotJoiningReason('');
                  }}
                  showAllOption={false}
                  placeholder="Formen seçin..."
                  searchPlaceholder="Formen ara..."
                  options={personnel
                    .filter(p => p.role === 'formen')
                    .filter(p => {
                      // Müdür herkesi görebilir
                      if (isMudur) return true;
                      // Müdür yardımcısı kendi bölge mühendislerine bağlı formenleri görebilir
                      if (isMudurYardimcisi) return p.mudur_yardimcisi_id === personnelId;
                      // Bölge mühendisi kendi formenlerini görebilir
                      if (isBolgeMuhendisi) return p.bolge_muhendisi_id === personnelId;
                      return false;
                    })
                    .sort((a, b) => a.name.localeCompare(b.name, 'tr'))
                    .map(f => ({ id: f.id, name: f.name }))}
                />
                
                {/* Formen Katılım Durumu */}
                {selectedMobileFormen && (
                  <div className="p-3 bg-white rounded-lg border border-blue-100 space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-gray-700">
                        {personnel.find(p => p.id === selectedMobileFormen)?.name} mesaiye katılacak mı?
                      </span>
                      <div className="flex items-center gap-3">
                        <label className="flex items-center gap-1 cursor-pointer">
                          <input 
                            type="radio" 
                            name="formenJoining" 
                            checked={formenJoining} 
                            onChange={() => {
                              setFormenJoining(true);
                              setFormenNotJoiningReason('');
                            }}
                            className="w-4 h-4 text-green-600"
                          />
                          <span className="text-sm text-green-700">✓ Evet</span>
                        </label>
                        <label className="flex items-center gap-1 cursor-pointer">
                          <input 
                            type="radio" 
                            name="formenJoining" 
                            checked={!formenJoining} 
                            onChange={() => setFormenJoining(false)}
                            className="w-4 h-4 text-red-600"
                          />
                          <span className="text-sm text-red-700">✗ Hayır</span>
                        </label>
                      </div>
                    </div>
                    
                    {/* Katılmıyorsa sebep */}
                    {!formenJoining && (
                      <div className="mt-2 p-2 bg-orange-50 rounded border border-orange-200">
                        <Label className="text-xs text-orange-700 mb-1 block">Katılmama Sebebi (Opsiyonel)</Label>
                        <Input
                          placeholder="Örn: İzinli, Raporlu, Başka görevde..."
                          value={formenNotJoiningReason}
                          onChange={(e) => setFormenNotJoiningReason(e.target.value)}
                          className="text-sm"
                        />
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
            
            {/* Formen kendisi için katılım seçeneği */}
            {isFormen && (
              <div className="p-3 bg-green-50 rounded-lg border border-green-200 space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-700">
                    Ben de mesaiye katılacağım
                  </span>
                  <div className="flex items-center gap-3">
                    <label className="flex items-center gap-1 cursor-pointer">
                      <input 
                        type="radio" 
                        name="formenSelfJoining" 
                        checked={formenJoining} 
                        onChange={() => {
                          setFormenJoining(true);
                          setFormenNotJoiningReason('');
                        }}
                        className="w-4 h-4 text-green-600"
                      />
                      <span className="text-sm text-green-700">✓ Evet</span>
                    </label>
                    <label className="flex items-center gap-1 cursor-pointer">
                      <input 
                        type="radio" 
                        name="formenSelfJoining" 
                        checked={!formenJoining} 
                        onChange={() => setFormenJoining(false)}
                        className="w-4 h-4 text-red-600"
                      />
                      <span className="text-sm text-red-700">✗ Hayır</span>
                    </label>
                  </div>
                </div>
                
                {/* Katılmıyorsa sebep */}
                {!formenJoining && (
                  <div className="mt-2 p-2 bg-orange-50 rounded border border-orange-200">
                    <Label className="text-xs text-orange-700 mb-1 block">Katılmama Sebebi (Opsiyonel)</Label>
                    <Input
                      placeholder="Örn: İzinli, Raporlu..."
                      value={formenNotJoiningReason}
                      onChange={(e) => setFormenNotJoiningReason(e.target.value)}
                      className="text-sm"
                    />
                  </div>
                )}
              </div>
            )}
            
            {/* Personel Arama */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Personel ara..."
                value={mobilePersonnelSearch}
                onChange={(e) => setMobilePersonnelSearch(e.target.value)}
                className="pl-10"
              />
            </div>
            
            {/* Seçilen Personeller */}
            {mobileTeamPersonnel.length > 0 && (
              <div className="flex flex-wrap gap-2 p-2 bg-purple-50 rounded-lg">
                {mobileTeamPersonnel.map(p => (
                  <Badge 
                    key={p.id} 
                    className="bg-purple-600 hover:bg-purple-700 cursor-pointer"
                    onClick={() => toggleMobileTeamPersonnel(p)}
                  >
                    {p.name}
                    <X className="h-3 w-3 ml-1" />
                  </Badge>
                ))}
              </div>
            )}
            
            {/* Personel Listesi - Hiyerarşik Filtreleme */}
            <div className="max-h-48 overflow-y-auto border rounded-lg divide-y">
              {(() => {
                // Hiyerarşik personel filtreleme
                let filteredPersonnel = personnel.filter(p => ['park_gorevlisi', 'çim_ekibi'].includes(p.role));
                
                if (isFormen) {
                  // Formen sadece kendine bağlı personeli görebilir
                  filteredPersonnel = filteredPersonnel.filter(p => p.formen_id === personnelId);
                } else if (['bolge_muhendisi', 'mudur_yardimcisi', 'mudur'].includes(userRole)) {
                  // Önce formen seçilmeli
                  if (selectedMobileFormen) {
                    filteredPersonnel = filteredPersonnel.filter(p => p.formen_id === selectedMobileFormen);
                  } else {
                    filteredPersonnel = []; // Formen seçilmeden personel gösterme
                  }
                }
                
                // Arama filtresi ve alfabetik sıralama
                return filteredPersonnel
                  .filter(p => mobilePersonnelSearch === '' ||
                    normalizeSearchText(p.name).includes(normalizeSearchText(mobilePersonnelSearch)) ||
                    normalizeSearchText(roleLabels[p.role] || p.role).includes(normalizeSearchText(mobilePersonnelSearch))
                  )
                  .sort((a, b) => a.name.localeCompare(b.name, 'tr'))
                  .map(person => {
                    const isSelected = mobileTeamPersonnel.some(p => p.id === person.id);
                    return (
                      <div 
                        key={person.id}
                        className={`flex items-center justify-between p-2 cursor-pointer hover:bg-gray-50 ${isSelected ? 'bg-purple-50' : ''}`}
                        onClick={() => toggleMobileTeamPersonnel(person)}
                      >
                        <div className="flex items-center gap-2">
                          <Checkbox checked={isSelected} />
                          <span className="font-medium">{person.name}</span>
                          <Badge variant="outline" className="text-xs">
                            {roleLabels[person.role] || person.role}
                          </Badge>
                        </div>
                      </div>
                    );
                  });
              })()}
              {/* Formen seçilmeden mesaj */}
              {['bolge_muhendisi', 'mudur_yardimcisi', 'mudur'].includes(userRole) && !selectedMobileFormen && (
                <div className="p-4 text-center text-gray-500 text-sm">
                  Personel listesini görmek için önce bir formen seçin
                </div>
              )}
            </div>
          </div>

          {/* Not Alanı */}
          <div className="space-y-2">
            <Label>Not (Opsiyonel)</Label>
            <Textarea
              placeholder="Örn: Batı bölgesi parkları, ilaçlama görevi vb."
              value={mobileTeamNote}
              onChange={(e) => setMobileTeamNote(e.target.value)}
              rows={2}
            />
          </div>

          {/* Butonlar */}
          <div className="flex gap-2 pt-2">
            <Button 
              variant="outline" 
              className="flex-1"
              onClick={() => {
                setShowMobileTeamDialog(false);
                setMobileTeamPersonnel([]);
                setMobileTeamNote('');
                setMobilePersonnelSearch('');
                setSelectedMobileFormen(null);
                setSelectedMobileBolgeMuh(null);
                setFormenJoining(true);
                setFormenNotJoiningReason('');
                setBolgeMuhJoining(false);
                setBolgeMuhNotJoiningReason('');
              }}
            >
              İptal
            </Button>
            <Button 
              className="flex-1 bg-purple-600 hover:bg-purple-700"
              onClick={handleCreateMobileTeam}
              disabled={(mobileTeamPersonnel.length === 0 && !formenJoining) || loading}
            >
              {loading ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <Users className="h-4 w-4 mr-2" />
              )}
              Mobil Ekip Oluştur
            </Button>
          </div>
        </div>
      </DraggableModal>

      {/* Supervisor (Formen/BM) Mesai İptal Dialogu */}
      <AlertDialog open={showSupervisorCancelDialog} onOpenChange={setShowSupervisorCancelDialog}>
        <AlertDialogContent className="z-[10000] max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-orange-600">
              <XCircle className="h-5 w-5" />
              Mesai İptali
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-4 text-gray-700">
                {supervisorCancelTarget && (
                  <div className={`p-3 rounded-lg border ${
                    supervisorCancelTarget.personnel_role === 'formen' 
                      ? 'bg-violet-50 border-violet-200' 
                      : 'bg-cyan-50 border-cyan-200'
                  }`}>
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-lg">
                        {supervisorCancelTarget.personnel_role === 'formen' ? '👷' : '🔍'}
                      </span>
                      <span className="font-semibold">
                        {supervisorCancelTarget.personnel_name}
                      </span>
                      <Badge variant="outline" className={`text-xs ${
                        supervisorCancelTarget.personnel_role === 'formen' 
                          ? 'bg-violet-100 text-violet-700' 
                          : 'bg-cyan-100 text-cyan-700'
                      }`}>
                        {supervisorCancelTarget.personnel_role === 'formen' ? 'Formen' : 'Bölge Müh.'}
                      </Badge>
                    </div>
                    <div className="text-sm text-gray-600">
                      <div className="flex items-center gap-2">
                        <Clock className="h-3 w-3" />
                        <span>{supervisorCancelTarget.start_time || '08:00'} - {supervisorCancelTarget.end_time || '17:00'}</span>
                        <span className="text-gray-400">|</span>
                        <span>{supervisorCancelTarget.hours || 8} saat</span>
                      </div>
                    </div>
                  </div>
                )}
                
                <div className="space-y-3">
                  <div>
                    <Label className="text-sm font-medium">İptal Sebebi *</Label>
                    <SearchableSelect
                      value={supervisorCancelReason}
                      onChange={setSupervisorCancelReason}
                      className="mt-1"
                      showAllOption={false}
                      placeholder="Sebep seçin"
                      searchPlaceholder="Sebep ara..."
                      options={[
                        { id: 'mazeret', name: 'Mazeretli (Sağlık, Acil Durum)' },
                        { id: 'izin', name: 'İzinli' },
                        { id: 'gorev_degisikligi', name: 'Görev Değişikliği' },
                        { id: 'planlama_hatasi', name: 'Planlama Hatası' },
                        { id: 'diger', name: 'Diğer' },
                      ]}
                    />
                  </div>
                  
                  <div>
                    <Label className="text-sm font-medium">Açıklama (Opsiyonel)</Label>
                    <Textarea 
                      value={supervisorCancelNote}
                      onChange={(e) => setSupervisorCancelNote(e.target.value)}
                      placeholder="İptal sebebi hakkında detay..."
                      className="mt-1"
                      rows={2}
                    />
                  </div>
                </div>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel 
              className="hover:bg-gray-100"
              onClick={() => {
                setSupervisorCancelTarget(null);
                setSupervisorCancelReason('');
                setSupervisorCancelNote('');
              }}
            >
              Vazgeç
            </AlertDialogCancel>
            <AlertDialogAction 
              onClick={async () => {
                if (!supervisorCancelReason) {
                  toast.error('Lütfen iptal sebebi seçin');
                  return;
                }
                try {
                  const auth = JSON.parse(localStorage.getItem('parkBakimAuth') || '{}');
                  await axios.put(`${API}/api/overtime-records/${supervisorCancelTarget.id}/cancel`, {
                    cancelled_by: auth.personnelId,
                    cancelled_by_name: auth.personnelName || '',
                    cancel_reason: supervisorCancelReason,
                    cancel_note: supervisorCancelNote
                  });
                  toast.success(`${supervisorCancelTarget.personnel_name} mesaisi iptal edildi`);
                  fetchDutySchedules();
                  setShowSupervisorCancelDialog(false);
                  setSupervisorCancelTarget(null);
                  setSupervisorCancelReason('');
                  setSupervisorCancelNote('');
                  // Gün dialogunu yenile
                  setShowDayDialog(false);
                  setTimeout(() => setShowDayDialog(true), 100);
                } catch (error) {
                  toast.error('İptal işlemi başarısız: ' + (error.response?.data?.detail || error.message));
                }
              }}
              className="bg-orange-600 hover:bg-orange-700 text-white"
              disabled={!supervisorCancelReason}
            >
              <XCircle className="h-4 w-4 mr-2" />
              Mesaiyi İptal Et
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Supervisor (Formen/BM) Detay Dialogu */}
      <DraggableModal
        isOpen={showSupervisorDetailDialog}
        onClose={() => {
          setShowSupervisorDetailDialog(false);
          setSupervisorDetailRecords([]);
          setSupervisorDetailRole(null);
        }}
        title={supervisorDetailRole === 'formen' ? 'ALAN DENETİMİ - Mesai Detayı' : 'BÖLGE DENETİMİ - Mesai Detayı'}
        maxWidth="max-w-lg"
      >
        {supervisorDetailDate && supervisorDetailRecords.length > 0 && (
          <div className="space-y-4">
            {/* Geri Butonu */}
            <Button 
              variant="outline" 
              size="sm" 
              className="bg-blue-50 hover:bg-blue-100 text-blue-700 border-blue-300 font-medium"
              onClick={() => {
                setShowSupervisorDetailDialog(false);
                setTimeout(() => setShowDayDialog(true), 100);
              }}
            >
              <ChevronLeft className="h-5 w-5 mr-1" />
              ← Geri Dön
            </Button>

            {/* Tarih ve Görev Tipi */}
            <div className={`p-4 rounded-lg border ${
              supervisorDetailRole === 'formen' 
                ? 'bg-violet-50 border-violet-200' 
                : 'bg-cyan-50 border-cyan-200'
            }`}>
              <div className="flex items-center justify-between mb-2">
                <div>
                  <p className="text-sm text-gray-500">Tarih</p>
                  <p className="font-semibold text-lg">{format(supervisorDetailDate, 'd MMMM yyyy, EEEE', { locale: tr })}</p>
                </div>
                <Badge className={
                  getDayType(supervisorDetailDate)?.type === 'resmi_tatil' ? 'bg-red-200 text-red-800' :
                  getDayType(supervisorDetailDate)?.type === 'idari_tatil' ? 'bg-purple-200 text-purple-800' :
                  [0, 6].includes(supervisorDetailDate.getDay()) ? 'bg-blue-200 text-blue-800' :
                  'bg-gray-200'
                }>
                  {getDayType(supervisorDetailDate)?.name || (supervisorDetailDate.getDay() === 0 ? 'Pazar' : supervisorDetailDate.getDay() === 6 ? 'Cumartesi' : 'Hafta İçi')}
                </Badge>
              </div>
              <div className={`text-xl font-bold ${
                supervisorDetailRole === 'formen' ? 'text-violet-700' : 'text-cyan-700'
              }`}>
                {supervisorDetailRole === 'formen' ? '👷 ALAN DENETİMİ' : '🔍 BÖLGE DENETİMİ'}
              </div>
            </div>

            {/* Atanan Personel Tablosu */}
            <div className="space-y-3">
              <h4 className="font-semibold text-gray-700">
                Atanan Personel ({supervisorDetailRecords.length})
              </h4>
              
              {/* Tablo Başlıkları */}
              <div className="grid grid-cols-12 gap-2 px-3 py-2 bg-gray-100 rounded-t-lg text-xs font-semibold text-gray-600">
                <div className="col-span-4">Personel</div>
                <div className="col-span-3 text-center">Saat Aralığı</div>
                <div className="col-span-2 text-center">Süre</div>
                <div className="col-span-3 text-center">İşlem</div>
              </div>
              
              {/* Tablo İçeriği */}
              {supervisorDetailRecords.map((record) => (
                <div 
                  key={record.id} 
                  className={`grid grid-cols-12 gap-2 px-3 py-3 border rounded-lg items-center ${
                    record.cancelled 
                      ? 'bg-red-50 border-red-200 opacity-60'
                      : 'bg-white border-gray-200'
                  }`}
                >
                  {/* Personel */}
                  <div className="col-span-4">
                    <div className={`font-medium ${record.cancelled ? 'line-through text-gray-500' : 'text-gray-800'}`}>
                      {record.personnel_name}
                    </div>
                    <div className={`text-xs ${
                      record.personnel_role === 'formen' ? 'text-violet-600' : 'text-cyan-600'
                    }`}>
                      {record.personnel_role === 'formen' ? 'FORMEN' : 'BÖLGE MÜH.'}
                    </div>
                  </div>
                  
                  {/* Saat Aralığı */}
                  <div className="col-span-3 text-center text-sm">
                    {record.start_time || '08:00'} - {record.end_time || '17:00'}
                  </div>
                  
                  {/* Süre */}
                  <div className="col-span-2 text-center text-sm font-medium">
                    {record.hours || 8} saat
                  </div>
                  
                  {/* İşlem */}
                  <div className="col-span-3 text-center">
                    {record.cancelled ? (
                      <div className="flex items-center justify-center gap-2">
                        <Badge variant="outline" className="bg-red-100 text-red-600 border-red-300 text-xs">
                          İPTAL
                        </Badge>
                        {canMutateMesai && (
                          <Button 
                            variant="ghost" 
                            size="sm"
                            className="hover:bg-green-100 h-7 px-2"
                            onClick={async () => {
                              try {
                                await axios.put(`${API}/api/overtime-records/${record.id}/restore`);
                                toast.success(`${record.personnel_name} mesaisi geri alındı`);
                                // Dialog'u güncelle
                                const updatedRecords = supervisorDetailRecords.map(r => 
                                  r.id === record.id 
                                    ? {...r, cancelled: false, cancelled_by_name: null, cancel_reason: null, cancel_note: null}
                                    : r
                                );
                                setSupervisorDetailRecords(updatedRecords);
                                fetchDutySchedules();
                              } catch (error) {
                                toast.error('Geri alma başarısız: ' + (error.response?.data?.detail || error.message));
                              }
                            }}
                          >
                            <RotateCcw className="h-4 w-4 text-green-600 mr-1" />
                            <span className="text-xs text-green-600">Geri Al</span>
                          </Button>
                        )}
                      </div>
                    ) : canMutateMesai ? (
                      <Button 
                        variant="ghost" 
                        size="sm"
                        className="hover:bg-red-100 h-7 px-2"
                        onClick={() => {
                          setSupervisorCancelTarget(record);
                          setSupervisorCancelReason('');
                          setSupervisorCancelNote('');
                          setShowSupervisorCancelDialog(true);
                        }}
                      >
                        <XCircle className="h-4 w-4 text-red-500 mr-1" />
                        <span className="text-xs text-red-600">İptal Et</span>
                      </Button>
                    ) : (
                      <span className="text-xs text-gray-400">-</span>
                    )}
                  </div>
                </div>
              ))}
              
              {/* İptal Detayı */}
              {supervisorDetailRecords.some(r => r.cancelled && r.cancelled_by_name) && (
                <div className="text-xs text-red-600 mt-2 p-2 bg-red-50 rounded">
                  {supervisorDetailRecords.filter(r => r.cancelled).map(r => (
                    <div key={r.id}>
                      İptal eden: {r.cancelled_by_name} {r.cancel_reason && `(${r.cancel_reason})`}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Toplam */}
            <div className="flex items-center justify-between p-3 bg-gray-100 rounded-lg border">
              <span className="font-semibold text-gray-700">Toplam:</span>
              <span className="text-gray-600">
                {supervisorDetailRecords.filter(r => !r.cancelled).length} personel, {' '}
                {supervisorDetailRecords.filter(r => !r.cancelled).reduce((sum, r) => sum + (r.hours || 8), 0)} saat mesai
              </span>
            </div>
          </div>
        )}
      </DraggableModal>

      {/* Silme Onay Dialogu */}
      <AlertDialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <AlertDialogContent className="z-[10000]">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="h-5 w-5" />
              Silme Onayı
            </AlertDialogTitle>
            <AlertDialogDescription className="text-gray-700">
              {deleteType === 'duty' && (
                <>
                  <strong>{deleteItemName}</strong> parkı için oluşturulan mesai planını silmek istediğinize emin misiniz?
                  <br />
                  <span className="text-sm text-red-500 mt-2 block">Bu işlem geri alınamaz!</span>
                </>
              )}
              {deleteType === 'holiday' && (
                <>
                  <strong>{deleteItemName}</strong> tatilini silmek istediğinize emin misiniz?
                  <br />
                  <span className="text-sm text-red-500 mt-2 block">Bu işlem geri alınamaz!</span>
                </>
              )}
              {deleteType === 'holidayFromDay' && (
                <>
                  <strong>{deleteItemName}</strong> tatilini bu günden kaldırmak istediğinize emin misiniz?
                  <br />
                  <span className="text-sm text-red-500 mt-2 block">Bu işlem geri alınamaz!</span>
                </>
              )}
              {deleteType === 'holidayAudit' && (
                <>
                  <strong>{deleteItemName}</strong> için işlem geçmişi kaydını silmek istediğinize emin misiniz?
                  <br />
                  <span className="text-sm text-red-500 mt-2 block">Bu işlem geri alınamaz!</span>
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="hover:bg-gray-100" disabled={deleteInProgress}>İptal</AlertDialogCancel>
            <AlertDialogAction 
              onClick={(e) => {
                e.preventDefault();
                if (!deleteInProgress) handleConfirmedDelete();
              }}
              disabled={deleteInProgress}
              className="bg-red-600 hover:bg-red-700 text-white disabled:opacity-70 disabled:pointer-events-none"
            >
              {deleteInProgress ? (
                <>
                  <span className="inline-block h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                  İşleniyor...
                </>
              ) : (
                <>
                  <Trash2 className="h-4 w-4 mr-2" />
                  Evet, Sil
                </>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
